import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as tus from "tus-js-client";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender
} from "@tanstack/react-table";
import { FaTrash} from "react-icons/fa"
import { FiEdit2, FiBarChart2, FiDownload } from "react-icons/fi";
import "./CSS/AdminAnalysisPage.css";
import supabase from "../supabaseClient";

// Simple debug logger to help inspect sorting/classification
const logDebug = (...args) => {
  // toggle this flag to silence logs quickly if needed
  const enabled = true;
  if (enabled) console.log("[AdminAnalysis]", ...args);
};

const API = process.env.REACT_APP_API_BASE_URL;
const columnHelper = createColumnHelper();
const STUDY1_END = new Date("2025-09-01T00:00:00Z");
const CHILD_RESPONSE_OPTIONS = [
  "Multi word response",
  "Single word response",
  "Gesture response",
  "Vocal response",
  "Multi word initiation",
  "Single word initiation",
  "Vocal initiation",
  "Gesture initiation",
];
const EDIT_VARIABLES = [
  { id: "time", label: "Time", historyKey: "begin_end" },
  { id: "strategy_detection", label: "Strategy Detection", historyKey: "strategy" },
  { id: "fidelity_score", label: "Fidelity Score", historyKey: "fidelity_score" },
  { id: "reasoning", label: "Reasoning", historyKey: "ai_reasoning" },
  { id: "child_response", label: "Child Response", historyKey: "child_response" },
  { id: "child_transcript", label: "Child Transcript", historyKey: "all_sentences" },
];
const CHILD_EDIT_VARIABLE_IDS = new Set(["child_response", "child_transcript"]);
const GRAPH_PHASES = [
  { id: "baseline", label: "Baseline", chartFill: "rgba(37, 99, 235, 0.12)" },
  { id: "treatment", label: "Treatment", chartFill: "rgba(16, 185, 129, 0.12)" },
  { id: "endline", label: "Follow-Up", chartFill: "rgba(217, 119, 6, 0.12)" },
];

const getSessionPhaseMeta = (phaseId) => {
  const normalized = String(phaseId || "").trim().toLowerCase();
  if (!normalized) return null;
  return GRAPH_PHASES.find((phase) => phase.id === normalized) || null;
};

const getStudyLabel = (createdAt) => {
  const ts = createdAt ? new Date(createdAt).getTime() : 0;
  return ts && ts < STUDY1_END.getTime() ? "User Study 1" : "User Study 2";
};

function parseBeginEndStart(beginEnd) {
  const first = String(beginEnd || "").split("-")[0].trim();
  if (!first) return null;
  return parseTimestampToSeconds(first);
}

function parseTimestampToSeconds(raw) {
  if (!raw) return null;
  const nums = String(raw)
    .split(":")
    .map((n) => Number(n.trim()));
  if (!nums.length || nums.some((n) => Number.isNaN(n))) return null;
  if (nums.length === 3) {
    const [h, m, s] = nums;
    return h * 3600 + m * 60 + s;
  }
  if (nums.length === 2) {
    const [m, s] = nums;
    return m * 60 + s;
  }
  return null;
}

function normalizeStrategyKey(strategy) {
  const raw = String(strategy || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw.includes("time delay")) return "time_delay";
  if (raw.includes("mand-model") || raw.includes("mand model")) return "mand_model";
  if (raw.includes("modeling")) return "modeling";
  return null;
}

function hasChildResponseValue(response) {
  const raw = String(response ?? "").trim().toLowerCase();
  if (!raw) return false;
  if (raw === "none" || raw === "na" || raw === "n/a" || raw === "-") return false;
  return true;
}

function normalizeChildResponseLabel(response) {
  if (!hasChildResponseValue(response)) return null;
  const raw = String(response ?? "").trim();
  const normalized = raw.toLowerCase();
  const matched = CHILD_RESPONSE_OPTIONS.find(
    (option) => option.toLowerCase() === normalized
  );
  return matched || raw;
}

function normalizeHistoryValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function isPlaceholderValue(value) {
  const normalized = String(value ?? "").trim();
  return !normalized || normalized === "-" || normalized.toLowerCase() === "na" || normalized.toLowerCase() === "n/a";
}

function isValidScoredStrategyRow(row = {}) {
  const strategyKey = normalizeStrategyKey(row.strategy);
  const score = Number(row.fidelity_score);
  return !!strategyKey && Number.isFinite(score) && score > 0 && !isPlaceholderValue(row.strategy);
}

function isParentProvidedOpportunityScore(score) {
  return score === 2 || score === 3 || score === 4;
}

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function sanitizeFilenamePart(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "") || "user";
}

function csvEscape(value) {
  const normalized = value === null || value === undefined ? "" : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

const ZIP_CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let j = 0; j < 8; j += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = ZIP_CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function dateToDosParts(date = new Date()) {
  const year = Math.max(1980, date.getFullYear());
  const dosTime =
    (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { dosTime, dosDate };
}

function makeZipBlob(files) {
  const encoder = new TextEncoder();
  const chunks = [];
  const centralChunks = [];
  let offset = 0;
  const { dosTime, dosDate } = dateToDosParts();

  files.forEach(({ name, content }) => {
    const nameBytes = encoder.encode(name);
    const data = typeof content === "string" ? encoder.encode(content) : content;
    const crc = crc32(data);

    const localHeader = new Uint8Array(30);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true);
    localView.setUint16(4, 20, true);
    localView.setUint16(6, 0, true);
    localView.setUint16(8, 0, true);
    localView.setUint16(10, dosTime, true);
    localView.setUint16(12, dosDate, true);
    localView.setUint32(14, crc, true);
    localView.setUint32(18, data.length, true);
    localView.setUint32(22, data.length, true);
    localView.setUint16(26, nameBytes.length, true);
    localView.setUint16(28, 0, true);

    chunks.push(localHeader, nameBytes, data);

    const centralHeader = new Uint8Array(46);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint16(8, 0, true);
    centralView.setUint16(10, 0, true);
    centralView.setUint16(12, dosTime, true);
    centralView.setUint16(14, dosDate, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint16(30, 0, true);
    centralView.setUint16(32, 0, true);
    centralView.setUint16(34, 0, true);
    centralView.setUint16(36, 0, true);
    centralView.setUint32(38, 0, true);
    centralView.setUint32(42, offset, true);
    centralChunks.push(centralHeader, nameBytes);

    offset += localHeader.length + nameBytes.length + data.length;
  });

  const centralSize = centralChunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(4, 0, true);
  endView.setUint16(6, 0, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, offset, true);
  endView.setUint16(20, 0, true);

  return new Blob([...chunks, ...centralChunks, endRecord], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function excelColumnName(index) {
  let n = index + 1;
  let name = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    name = String.fromCharCode(65 + rem) + name;
    n = Math.floor((n - 1) / 26);
  }
  return name;
}

function makeWorksheetXml(rows, columnWidths = [], drawingRelId = "") {
  const rowXml = rows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, colIndex) => {
          const isObjectCell = cell && typeof cell === "object" && !Array.isArray(cell);
          const value = isObjectCell ? cell.value : cell;
          const style = isObjectCell && cell.style ? ` s="${cell.style}"` : "";
          const ref = `${excelColumnName(colIndex)}${rowIndex + 1}`;
          if (isObjectCell && cell.error) {
            return `<c r="${ref}" t="e"${style}><v>${escapeXml(cell.error)}</v></c>`;
          }
          if (isObjectCell && cell.formula) {
            return `<c r="${ref}"${style}><f>${escapeXml(cell.formula)}</f></c>`;
          }
          if (value === null || value === undefined || value === "") return `<c r="${ref}"${style}/>`;
          if (typeof value === "number" && Number.isFinite(value)) {
            return `<c r="${ref}"${style}><v>${value}</v></c>`;
          }
          return `<c r="${ref}" t="inlineStr"${style}><is><t>${escapeXml(value)}</t></is></c>`;
        })
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");

  const colsXml = columnWidths.length
    ? `<cols>${columnWidths
        .map(
          (width, index) =>
            `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`
        )
        .join("")}</cols>`
    : "";

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  ${colsXml}
  <sheetData>${rowXml}</sheetData>
  ${drawingRelId ? `<drawing r:id="${drawingRelId}"/>` : ""}
</worksheet>`;
}

function makeImageDrawingXml({ relId, widthPx, heightPx, name = "Graph" }) {
  const cx = Math.round((widthPx || 1200) * 9525);
  const cy = Math.round((heightPx || 800) * 9525);
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <xdr:absoluteAnchor>
    <xdr:pos x="0" y="0"/>
    <xdr:ext cx="${cx}" cy="${cy}"/>
    <xdr:pic>
      <xdr:nvPicPr>
        <xdr:cNvPr id="2" name="${escapeXml(name)}"/>
        <xdr:cNvPicPr/>
      </xdr:nvPicPr>
      <xdr:blipFill>
        <a:blip r:embed="${relId}"/>
        <a:stretch><a:fillRect/></a:stretch>
      </xdr:blipFill>
      <xdr:spPr>
        <a:xfrm>
          <a:off x="0" y="0"/>
          <a:ext cx="${cx}" cy="${cy}"/>
        </a:xfrm>
        <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
      </xdr:spPr>
    </xdr:pic>
    <xdr:clientData/>
  </xdr:absoluteAnchor>
</xdr:wsDr>`;
}

function makeXlsxWorkbookBlob(sheets) {
  const safeSheetName = (name) =>
    String(name || "Sheet")
      .split("")
      .map((char) => ('[]*?/\\:'.includes(char) ? " " : char))
      .join("")
      .slice(0, 31);

  const workbookSheets = sheets
    .map(
      (sheet, index) =>
        `<sheet name="${escapeXml(safeSheetName(sheet.name))}" sheetId="${index + 1}" r:id="rId${
          index + 1
        }"/>`
    )
    .join("");
  const workbookRels = sheets
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${
          index + 1
        }.xml"/>`
    )
    .join("");
  const sheetOverrides = sheets
    .map(
      (_, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join("");
  const drawingOverrides = sheets
    .map((sheet, index) =>
      sheet.drawing || sheet.chartDrawing
        ? `<Override PartName="/xl/drawings/drawing${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`
        : ""
    )
    .join("");
  const chartOverrides = sheets
    .flatMap((sheet) => sheet.chartDrawing?.charts || [])
    .map(
      (chart) =>
        `<Override PartName="/xl/charts/${chart.name}" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`
    )
    .join("");

  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Default Extension="svg" ContentType="image/svg+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetOverrides}
  ${drawingOverrides}
  ${chartOverrides}
</Types>`,
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`,
    },
    {
      name: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <bookViews><workbookView activeTab="0"/></bookViews>
  <sheets>${workbookSheets}</sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${workbookRels}
  <Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><name val="Calibri"/></font>
    <font><b/><sz val="11"/><name val="Calibri"/></font>
  </fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
    },
    {
      name: "docProps/app.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <Application>PaiCoach</Application>
</Properties>`,
    },
    {
      name: "docProps/core.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Overall Participant Performance</dc:title>
  <dc:creator>PaiCoach</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${new Date().toISOString()}</dcterms:created>
</cp:coreProperties>`,
    },
  ];

  sheets.forEach((sheet, index) => {
    const sheetNumber = index + 1;
    const drawingRelId = sheet.drawing || sheet.chartDrawing ? "rId1" : "";
    files.push({
      name: `xl/worksheets/sheet${sheetNumber}.xml`,
      content: makeWorksheetXml(sheet.rows, sheet.columnWidths, drawingRelId),
    });

    if (sheet.drawing) {
      const mediaName = `image${sheetNumber}.svg`;
      files.push({
        name: `xl/worksheets/_rels/sheet${sheetNumber}.xml.rels`,
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${sheetNumber}.xml"/>
</Relationships>`,
      });
      files.push({
        name: `xl/drawings/drawing${sheetNumber}.xml`,
        content: makeImageDrawingXml({
          relId: "rId1",
          widthPx: sheet.drawing.widthPx,
          heightPx: sheet.drawing.heightPx,
          name: sheet.drawing.name || sheet.name,
        }),
      });
      files.push({
        name: `xl/drawings/_rels/drawing${sheetNumber}.xml.rels`,
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${mediaName}"/>
</Relationships>`,
      });
      files.push({
        name: `xl/media/${mediaName}`,
        content: sheet.drawing.content,
      });
    }

    if (sheet.chartDrawing) {
      files.push({
        name: `xl/worksheets/_rels/sheet${sheetNumber}.xml.rels`,
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing${sheetNumber}.xml"/>
</Relationships>`,
      });
      files.push({
        name: `xl/drawings/drawing${sheetNumber}.xml`,
        content: sheet.chartDrawing.xml,
      });
      files.push({
        name: `xl/drawings/_rels/drawing${sheetNumber}.xml.rels`,
        content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheet.chartDrawing.charts
    .map(
      (chart, chartIndex) =>
        `<Relationship Id="rId${chartIndex + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/${chart.name}"/>`
    )
    .join("")}
</Relationships>`,
      });
      sheet.chartDrawing.charts.forEach((chart) => {
        files.push({
          name: `xl/charts/${chart.name}`,
          content: chart.xml,
        });
      });
    }
  });

  return makeZipBlob(files);
}

function downloadXlsxFile(filename, sheets) {
  const blob = makeXlsxWorkbookBlob(sheets);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function excelCellRef(rowNumber, colIndex) {
  return `$${excelColumnName(colIndex)}$${rowNumber}`;
}

function excelSheetRef(sheetName, startRow, startCol, endRow = startRow, endCol = startCol) {
  const escapedSheet = String(sheetName).replace(/'/g, "''");
  const start = excelCellRef(startRow, startCol);
  const end = excelCellRef(endRow, endCol);
  return `'${escapedSheet}'!${start}${start === end ? "" : `:${end}`}`;
}

function makeLineChartSeriesXml({
  index,
  nameRef,
  categoriesRef,
  valuesRef,
  color,
  dashed = false,
  marker = "circle",
  smooth = null,
}) {
  return `<c:ser>
    <c:idx val="${index}"/>
    <c:order val="${index}"/>
    ${nameRef ? `<c:tx><c:strRef><c:f>${escapeXml(nameRef)}</c:f></c:strRef></c:tx>` : ""}
    <c:spPr>
      <a:ln w="28575">
        <a:solidFill><a:srgbClr val="${color}"/></a:solidFill>
        ${dashed ? '<a:prstDash val="dash"/>' : ""}
      </a:ln>
    </c:spPr>
    <c:marker>
      <c:symbol val="${marker}"/>
      <c:size val="6"/>
      <c:spPr>
        <a:solidFill><a:srgbClr val="${dashed ? "FFFFFF" : color}"/></a:solidFill>
        <a:ln w="19050"><a:solidFill><a:srgbClr val="${color}"/></a:solidFill></a:ln>
      </c:spPr>
    </c:marker>
    <c:cat><c:strRef><c:f>${escapeXml(categoriesRef)}</c:f></c:strRef></c:cat>
    <c:val><c:numRef><c:f>${escapeXml(valuesRef)}</c:f></c:numRef></c:val>
    ${smooth === null ? "" : `<c:smooth val="${smooth ? 1 : 0}"/>`}
  </c:ser>`;
}

function makeEditableLineChartXml({
  title,
  categoriesRef,
  parentNameRef,
  parentValuesRef,
  childNameRef,
  childValuesRef,
  extraSeries = [],
  showXAxis = false,
  showLegend = false,
}) {
  const catAxisId = 100001;
  const valAxisId = 100002;
  const chartMin = 0;
  const chartMax = 100;
  const hiddenLegendEntries = extraSeries
    .map(
      (_series, extraIndex) =>
        `<c:legendEntry><c:idx val="${extraIndex + 2}"/><c:delete val="1"/></c:legendEntry>`
    )
    .join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:date1904 val="0"/>
  <c:lang val="en-US"/>
  <c:roundedCorners val="0"/>
  <c:chart>
    <c:title>
      <c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:r><a:rPr lang="en-US" sz="1400" b="1"><a:latin typeface="Times New Roman"/></a:rPr><a:t>${escapeXml(
        title
      )}</a:t></a:r></a:p></c:rich></c:tx>
      <c:layout/>
      <c:overlay val="0"/>
    </c:title>
    <c:plotArea>
      <c:layout/>
      <c:lineChart>
        <c:grouping val="standard"/>
        <c:varyColors val="0"/>
        ${makeLineChartSeriesXml({
          index: 0,
          nameRef: parentNameRef,
          categoriesRef,
          valuesRef: parentValuesRef,
          color: "1F2937",
          marker: "circle",
          smooth: true,
        })}
        ${makeLineChartSeriesXml({
          index: 1,
          nameRef: childNameRef,
          categoriesRef,
          valuesRef: childValuesRef,
          color: "4B5563",
          dashed: true,
          marker: "triangle",
          smooth: true,
        })}
        ${extraSeries
          .map((series, extraIndex) =>
            makeLineChartSeriesXml({
              index: extraIndex + 2,
              nameRef: series.nameRef || null,
              categoriesRef,
              valuesRef: series.valuesRef,
              color: series.color,
              dashed: !!series.dashed,
              marker: series.marker || "circle",
              smooth: false,
            })
          )
          .join("")}
        <c:axId val="${catAxisId}"/>
        <c:axId val="${valAxisId}"/>
      </c:lineChart>
      <c:catAx>
        <c:axId val="${catAxisId}"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="${showXAxis ? 0 : 1}"/>
        <c:axPos val="b"/>
        <c:majorTickMark val="out"/>
        <c:minorTickMark val="none"/>
        <c:tickLblPos val="${showXAxis ? "nextTo" : "none"}"/>
        <c:crossAx val="${valAxisId}"/>
        <c:crosses val="autoZero"/>
        <c:lblAlgn val="ctr"/>
        <c:lblOffset val="100"/>
      </c:catAx>
      <c:valAx>
        <c:axId val="${valAxisId}"/>
        <c:scaling><c:orientation val="minMax"/><c:max val="${chartMax}"/><c:min val="${chartMin}"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="l"/>
        <c:numFmt formatCode="0" sourceLinked="0"/>
        <c:majorUnit val="20"/>
        <c:majorTickMark val="out"/>
        <c:minorTickMark val="none"/>
        <c:tickLblPos val="nextTo"/>
        <c:crossAx val="${catAxisId}"/>
        <c:crosses val="autoZero"/>
      </c:valAx>
    </c:plotArea>
    ${
      showLegend
        ? `<c:legend><c:legendPos val="t"/>${hiddenLegendEntries}<c:overlay val="0"/></c:legend>`
        : '<c:legend><c:delete val="1"/></c:legend>'
    }
    <c:plotVisOnly val="1"/>
    <c:dispBlanksAs val="gap"/>
  </c:chart>
</c:chartSpace>`;
}

function makeStackedChartDrawingXml(chartCount) {
  const anchors = Array.from({ length: chartCount }, (_, index) => {
    const fromRow = index * 14;
    const toRow = fromRow + 13;
    return `<xdr:twoCellAnchor>
      <xdr:from><xdr:col>0</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${fromRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
      <xdr:to><xdr:col>18</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${toRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
      <xdr:graphicFrame macro="">
        <xdr:nvGraphicFramePr>
          <xdr:cNvPr id="${index + 2}" name="Chart ${index + 1}"/>
          <xdr:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></xdr:cNvGraphicFramePr>
        </xdr:nvGraphicFramePr>
        <xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>
        <a:graphic>
          <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
            <c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" r:id="rId${
              index + 1
            }"/>
          </a:graphicData>
        </a:graphic>
      </xdr:graphicFrame>
      <xdr:clientData/>
    </xdr:twoCellAnchor>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  ${anchors}
</xdr:wsDr>`;
}

function splitOverallGraphLabel(value) {
  const text = String(value || "").trim();
  if (!text) return ["Unnamed user"];
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [text];

  const first = words[0];
  const second = words.slice(1).join(" ");

  if (!second) return [text];
  return [first, second];
}

function formatDateTimeLocalValue(date = new Date()) {
  const pad = (value) => String(value).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function EditableCell({ initial, displayValue, onSave, type = "text", readOnly = false, isEditing = false, onStart, onStop, options }) {
  const [val, setVal] = useState(initial ?? "");

  useEffect(() => {
    if (!isEditing) {
      setVal(initial ?? "");
    }
  }, [initial, isEditing]);

  if (readOnly) {
    return <span className="cell-wrap readonly">{displayValue ?? initial ?? <i className="muted">—</i>}</span>;
  }

  if (!isEditing) {
    return (
      <span className="cell-wrap">
        {displayValue ?? initial ?? <i className="muted">—</i>}
        <button className="edit-btn" onClick={onStart}>
          <FiEdit2 size={14} />
        </button>
      </span>
    );
  }

  const renderInput = () => {
    if (type === "strategy") {
      return (
        <select value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">Select strategy</option>
          <option value="Mand-model">Mand-model</option>
          <option value="Modeling">Modeling</option>
          <option value="Time Delay">Time Delay</option>
        </select>
      );
    }
    if (type === "score") {
      return (
        <select value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">Select score</option>
          {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      );
    }
    if (type === "child-response") {
      return (
        <select value={val} onChange={(e) => setVal(e.target.value)}>
          <option value="">Select response</option>
          {(options || []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return (
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={3}
      />
    );
  };

  return (
    <div className="edit-box">
      {renderInput()}
      <button
        className="save-btn"
        onClick={async () => {
          const id = await onSave(val);
          onStop(id);
        }}
      >
        Save
      </button>
      <button
        className="icon-button secondary"
        onClick={() => {
          setVal(initial ?? "");
          onStop();
        }}
      >
        Cancel
      </button>
    </div>
  );
}

function VideoPreview({
  videoKey,
  resumeTime = 0,
  seekRequest,
  onTimeUpdate,
  onOrientationChange,
  onDurationLoaded,
}) {
  const [videoUrl, setVideoUrl] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoKey) return;
    const API = process.env.REACT_APP_API_BASE_URL;
    const safeKey = encodeURIComponent(videoKey);

    setVideoUrl(`${API}/api/video/${safeKey}`);
  }, [videoKey]);

  useEffect(() => {
    if (!videoRef.current) return;
    const node = videoRef.current;
    const handler = () => {
      if (onTimeUpdate) onTimeUpdate(node.currentTime || 0);
    };
    node.addEventListener("timeupdate", handler);
    return () => node.removeEventListener("timeupdate", handler);
  }, [onTimeUpdate]);

  useEffect(() => {
    if (!videoRef.current || !seekRequest) return;
    if (typeof seekRequest.seconds !== "number") return;
    videoRef.current.currentTime = seekRequest.seconds;
  }, [seekRequest]);

  useEffect(() => {
    if (!videoKey && onOrientationChange) {
      onOrientationChange(false);
    }
  }, [videoKey, onOrientationChange]);

  const handleLoadedMetadata = () => {
    if (!videoRef.current || !onOrientationChange) return;
    const { videoWidth, videoHeight } = videoRef.current;
    if (videoWidth && videoHeight) {
      onOrientationChange(videoHeight > videoWidth);
    }
    if (onDurationLoaded && Number.isFinite(videoRef.current.duration)) {
      onDurationLoaded(videoRef.current.duration);
    }
    if (videoRef.current && resumeTime > 0) {
      videoRef.current.currentTime = resumeTime;
    }
  };

  return (
    <div className="video-preview">

      {videoUrl && (
        <video
          key={videoUrl}
          ref={videoRef}
          controls
          width="100%"
          playsInline
          preload="metadata"
          src={videoUrl}
          onLoadedMetadata={handleLoadedMetadata}
        />
      )}
    </div>
  );
}

function downloadCSV(rows) {
  if (!rows || !rows.length) return;

  const header = ["Begin-End", "Strategy", "Score", "Reasoning"];
  const csvRows = [
    header.join(","),
    ...rows.map(row =>
      [
        `"${row.begin_end ?? ""}"`,
        `"${row.strategy ?? ""}"`,
        `"${row.fidelity_score ?? ""}"`,
        `"${(row.ai_reasoning ?? "").replace(/\n/g, " ")}"`
      ].join(",")
    ),
  ];

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "analysis_data.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function downloadSvgFile(filename, svgMarkup) {
  const blob = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminAnalysisPage({ session, profile }) {
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedId, setSelectedId]   = useState(null);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingCells, setEditingCells] = useState({});
  const [viewMode, setViewMode] = useState("sessions"); // sessions | names
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [studyFilter, setStudyFilter] = useState("study2"); // all | study1 | study2
  const [videoTimes, setVideoTimes] = useState({});
  const [videoDurations, setVideoDurations] = useState({});
  const [seekRequest, setSeekRequest] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportingSummary, setIsImportingSummary] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [statsSessionId, setStatsSessionId] = useState(null);
  const [statsUserId, setStatsUserId] = useState(null);
  const [statsBrowseMode, setStatsBrowseMode] = useState(false);
  const [statsTab, setStatsTab] = useState("metrics");
  const [graphPhaseBySession, setGraphPhaseBySession] = useState({});
  const [activeGraphPhase, setActiveGraphPhase] = useState("");
  const [graphPhaseLoading, setGraphPhaseLoading] = useState(false);
  const [graphPhaseError, setGraphPhaseError] = useState("");
  const [statsInclusionBySession, setStatsInclusionBySession] = useState({});
  const [editsChildInclusionBySession, setEditsChildInclusionBySession] = useState({});
  const [statsInclusionLoading, setStatsInclusionLoading] = useState(false);
  const [statsInclusionError, setStatsInclusionError] = useState("");
  const [statsDataMode, setStatsDataMode] = useState("final"); // final | original
  const [originalStatsRows, setOriginalStatsRows] = useState([]);
  const [originalStatsLoading, setOriginalStatsLoading] = useState(false);
  const [originalStatsError, setOriginalStatsError] = useState("");
  const [editsStatsRows, setEditsStatsRows] = useState([]);
  const [editsLoading, setEditsLoading] = useState(false);
  const [editsError, setEditsError] = useState("");
  const [showBaselineUpload, setShowBaselineUpload] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUploadSubmitting, setAdminUploadSubmitting] = useState(false);
  const [adminUploadProgress, setAdminUploadProgress] = useState(0);
  const [adminUploadError, setAdminUploadError] = useState("");
  const [baselineUploadForm, setBaselineUploadForm] = useState({
    userId: "",
    createdAt: formatDateTimeLocalValue(),
    file: null,
  });
  const fileInputRef = useRef(null);
  const summaryInputRef = useRef(null);
  const baselineVideoInputRef = useRef(null);
  const statsModalRef = useRef(null);
  const statsModalDragRef = useRef(null);
  const statsModalResizeRef = useRef(null);
  const statsModalSuppressBackdropClickRef = useRef(false);
  const [statsModalFrame, setStatsModalFrame] = useState(null);
  const [adminTab, setAdminTab] = useState("analysis"); // analysis | reliability
  const navigate = useNavigate();

  const isReliabilityOnly = !!profile?.reliability_only;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/api/admin/analysis`, {
      headers: { Authorization: `Bearer ${session.access_token}` }
    });
    setRows(data);
    setLoading(false);
  }, [session.access_token]);

  const fetchHistory = useCallback(async (sessionId) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/analysis/history`,
        {
          params: { session_id: sessionId },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
      const normalized = (data || [])
        .filter((h) => h.action !== "create")
        .map((h) => ({
          ...h,
          historyId: h.id,
          segment_id: h.segment_id,
          begin_end: h.begin_end,
          strategy: h.strategy,
          fidelity_score: h.fidelity_score,
          ai_reasoning: h.ai_reasoning,
          child_response: h.child_response,
          all_sentences: h.all_sentences,
          admin_edited: false,
          isHistory: true,
          is_deleted: !!(h.is_deleted || h.action === "delete"),
          historyAction: h.action,
          historyCreatedAt: h.created_at,
        }));
      // Keep only the earliest "original" snapshot per segment; include all other actions
      const earliestOriginal = new Map();
      normalized.forEach((h) => {
        if (h.historyAction !== "original") return;
        const t = h.historyCreatedAt ? new Date(h.historyCreatedAt).getTime() : Infinity;
        const cur = earliestOriginal.get(h.segment_id);
        if (!cur || t < cur.time) {
          earliestOriginal.set(h.segment_id, { time: t, id: h.historyId });
        }
      });
      const earliestIds = new Set(Array.from(earliestOriginal.values()).map((v) => v.id));
      const filteredHistory = normalized.filter(
        (h) => h.historyAction !== "original" || earliestIds.has(h.historyId)
      );
      setHistoryRows(filteredHistory);
      logDebug("Fetched history", {
        sessionId,
        count: filteredHistory.length,
        actions: filteredHistory.reduce((acc, h) => {
          acc[h.historyAction] = (acc[h.historyAction] || 0) + 1;
          return acc;
        }, {})
      });
      setShowHistory(true);
    } catch (err) {
      console.error("History fetch failed:", err);
      alert("Failed to load history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [session.access_token]);

  useEffect(() => {
    if (showHistory && selectedId) {
      fetchHistory(selectedId);
    }
  }, [showHistory, selectedId, fetchHistory]);

  const handleSignOut = async () => {
  await supabase.auth.signOut();
  navigate("/login", { replace: true });
  };

  const handleSwitchToMain = () => {
    navigate("/Project-Demo");
  };

  const openBaselineUploadModal = useCallback((defaultUserId = "") => {
    setAdminUploadError("");
    setAdminUploadProgress(0);
    setBaselineUploadForm({
      userId: defaultUserId || selectedUserId || "",
      createdAt: formatDateTimeLocalValue(),
      file: null,
    });
    if (baselineVideoInputRef.current) {
      baselineVideoInputRef.current.value = "";
    }
    setShowBaselineUpload(true);
  }, [selectedUserId]);

  const closeBaselineUploadModal = useCallback(() => {
    setShowBaselineUpload(false);
    setAdminUploadError("");
    setAdminUploadProgress(0);
    setAdminUploadSubmitting(false);
  }, []);

  const openStatsForSession = useCallback((sessionId) => {
    if (!sessionId) return;
    setStatsBrowseMode(false);
    setStatsUserId(null);
    setStatsSessionId(sessionId);
    setStatsTab("metrics");
    setShowStats(true);
  }, []);

  const openStatsBrowser = useCallback(() => {
    setStatsBrowseMode(true);
    setStatsSessionId(null);
    setStatsUserId(null);
    setStatsTab("graph");
    setShowStats(true);
  }, []);

  const closeStatsModal = useCallback(() => {
    setShowStats(false);
  }, []);

  const handleStatsBackdropClick = useCallback((event) => {
    if (event.target !== event.currentTarget) return;
    if (statsModalSuppressBackdropClickRef.current) {
      statsModalSuppressBackdropClickRef.current = false;
      return;
    }
    closeStatsModal();
  }, [closeStatsModal]);

  const startStatsModalDrag = useCallback((event) => {
    if (event.button !== 0) return;
    if (event.target.closest("button, input, select, textarea, a")) return;
    const modal = statsModalRef.current;
    if (!modal) return;
    event.stopPropagation();
    statsModalSuppressBackdropClickRef.current = true;
    const rect = modal.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const width = rect.width;
    const height = rect.height;
    statsModalDragRef.current = { startX, startY, startLeft, startTop, width, height };
    setStatsModalFrame({
      left: startLeft,
      top: startTop,
      width,
      height,
    });
    event.preventDefault();
  }, []);

  const startStatsModalResize = useCallback((corner) => (event) => {
    if (event.button !== 0) return;
    const modal = statsModalRef.current;
    if (!modal) return;
    event.stopPropagation();
    event.preventDefault();
    statsModalSuppressBackdropClickRef.current = true;
    const rect = modal.getBoundingClientRect();
    statsModalResizeRef.current = {
      corner,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      startWidth: rect.width,
      startHeight: rect.height,
    };
    setStatsModalFrame({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    });
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      const drag = statsModalDragRef.current;
      const resize = statsModalResizeRef.current;
      if (!drag && !resize) return;
      statsModalSuppressBackdropClickRef.current = true;

      if (drag) {
        const nextLeft = drag.startLeft + event.clientX - drag.startX;
        const nextTop = drag.startTop + event.clientY - drag.startY;
        const maxLeft = Math.max(8, window.innerWidth - drag.width - 8);
        const maxTop = Math.max(8, window.innerHeight - drag.height - 8);
        setStatsModalFrame((prev) => ({
          left: Math.min(Math.max(8, nextLeft), maxLeft),
          top: Math.min(Math.max(8, nextTop), maxTop),
          width: prev?.width || drag.width,
          height: prev?.height || drag.height,
        }));
        return;
      }

      const minWidth = Math.min(620, window.innerWidth - 16);
      const minHeight = Math.min(420, window.innerHeight - 16);
      const maxWidth = window.innerWidth - 16;
      const maxHeight = window.innerHeight - 16;
      const dx = event.clientX - resize.startX;
      const dy = event.clientY - resize.startY;
      const west = resize.corner.includes("w");
      const north = resize.corner.includes("n");
      let nextLeft = resize.startLeft;
      let nextTop = resize.startTop;
      let nextWidth = resize.startWidth + (west ? -dx : dx);
      let nextHeight = resize.startHeight + (north ? -dy : dy);

      nextWidth = Math.min(Math.max(minWidth, nextWidth), maxWidth);
      nextHeight = Math.min(Math.max(minHeight, nextHeight), maxHeight);
      if (west) nextLeft = resize.startLeft + resize.startWidth - nextWidth;
      if (north) nextTop = resize.startTop + resize.startHeight - nextHeight;

      nextLeft = Math.min(Math.max(8, nextLeft), Math.max(8, window.innerWidth - nextWidth - 8));
      nextTop = Math.min(Math.max(8, nextTop), Math.max(8, window.innerHeight - nextHeight - 8));
      setStatsModalFrame({
        left: nextLeft,
        top: nextTop,
        width: nextWidth,
        height: nextHeight,
      });
    };
    const handleUp = () => {
      statsModalDragRef.current = null;
      statsModalResizeRef.current = null;
      setTimeout(() => {
        statsModalSuppressBackdropClickRef.current = false;
      }, 0);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    if (!showBaselineUpload) return;
    let cancelled = false;

    const loadAdminUsers = async () => {
      setAdminUsersLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/admin/users`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!cancelled) {
          setAdminUsers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to load admin upload users:", err);
        if (!cancelled) {
          setAdminUploadError("Failed to load users for baseline upload.");
        }
      } finally {
        if (!cancelled) {
          setAdminUsersLoading(false);
        }
      }
    };

    loadAdminUsers();
    return () => {
      cancelled = true;
    };
  }, [showBaselineUpload, session.access_token]);

  useEffect(() => {
    if (isReliabilityOnly && adminTab !== "reliability") {
      setAdminTab("reliability");
    }
  }, [isReliabilityOnly, adminTab]);

  // When switching sessions, turn off history view and clear cached history
  useEffect(() => {
    setHistoryRows([]);
    setHistoryLoading(false);
    setShowStats(false);
    setStatsSessionId(null);
    setStatsUserId(null);
    setStatsBrowseMode(false);
    setStatsTab("metrics");
    setActiveGraphPhase("");
    setGraphPhaseError("");
    setGraphPhaseLoading(false);
    setStatsInclusionBySession({});
    setStatsInclusionLoading(false);
    setStatsInclusionError("");
    setEditsStatsRows([]);
    setEditsLoading(false);
    setEditsError("");
  }, [selectedId]);

  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const deepId  = params.get("session");
    if (!deepId || selectedId) return;
    if (rows.some(r => r.session_id === deepId)) {
      setSelectedId(deepId);
    }
  }, [rows, selectedId]);


const save = useCallback(async (segmentId, patch) => {
  const target = rows.find(r => r.segment_id === segmentId);
  let resolvedId = segmentId;

  if (target?.isNew) {
    const { data } = await axios.post(`${API}/api/admin/analysis`, {
      session_id: target.session_id,
      ...patch
    }, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    setRows(prev =>
      prev.map(r =>
        r.segment_id === segmentId
          ? {
              ...data,
              ...patch,
              segment_id: data.id || data.segment_id || segmentId,
              isNew: false,
            }
          : r
      )
    );
    const newId = data.id || data.segment_id || segmentId;
    resolvedId = newId;
    setEditingCells(prev => {
      if (!prev[segmentId]) return prev;
      const next = { ...prev };
      next[newId] = { ...next[segmentId] };
      delete next[segmentId];
      return next;
    });
  } else {
    const { data } = await axios.put(`${API}/api/admin/analysis/${segmentId}`, patch, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    setRows(prev =>
      prev.map(r =>
        r.segment_id === segmentId ? { ...r, ...patch, ...data } : r
      )
    );
    if (showHistory && target) {
      const snapshotId = `local-original-${segmentId}-${Date.now()}`;
      setHistoryRows(prev => [
        {
          ...target,
          historyId: snapshotId,
          isHistory: true,
          historyAction: "original",
          historyCreatedAt: new Date().toISOString(),
          is_deleted: false,
        },
        ...prev,
      ]);
    }
  }
  return resolvedId;
}, [rows, session.access_token, showHistory]);

const startEditCell = useCallback((segmentId, field) => {
  setEditingCells(prev => ({
    ...prev,
    [segmentId]: { ...(prev[segmentId] || {}), [field]: true }
  }));
}, []);

const stopEditCell = useCallback((segmentId, field) => {
  setEditingCells(prev => {
    const next = { ...prev };
    if (next[segmentId]) {
      const { [field]: _removed, ...rest } = next[segmentId];
      if (Object.keys(rest).length) next[segmentId] = rest;
      else delete next[segmentId];
    }
    return next;
  });
}, []);

const handleRowDelete = useCallback(async (original) => {
  if (!window.confirm("Delete this row? This cannot be undone.")) return;

  if (original.isNew) {
    setRows(prev => prev.filter(r => r.segment_id !== original.segment_id));
    setEditingCells(prev => {
      if (!prev[original.segment_id]) return prev;
      const next = { ...prev };
      delete next[original.segment_id];
      return next;
    });
    return;
  }

  const previousRows = rows;
  const previousHistory = historyRows;

  // Optimistic UI update to preserve current scroll position.
  setRows(prev => prev.filter(r => r.segment_id !== original.segment_id));
  setEditingCells(prev => {
    if (!prev[original.segment_id]) return prev;
    const next = { ...prev };
    delete next[original.segment_id];
    return next;
  });

  try {
    await axios.delete(`${API}/api/admin/analysis/${original.segment_id}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    // Refresh history in background when visible, without forcing a full table refetch.
    if (showHistory && selectedId) {
      fetchHistory(selectedId);
    }
  } catch (err) {
    setRows(previousRows);
    setHistoryRows(previousHistory);
    console.error("delete row error:", err);
    alert("Failed to delete row.");
  }
}, [session.access_token, rows, historyRows, showHistory, selectedId, fetchHistory]);

const seekToSegmentStart = useCallback((beginEnd) => {
  const seconds = parseBeginEndStart(beginEnd);
  if (seconds === null || !selectedId) return;
  setSeekRequest({ seconds, nonce: Date.now() });
  setVideoTimes(prev => ({
    ...prev,
    [selectedId]: seconds,
  }));
}, [selectedId]);


const columns = useMemo(() => [
    columnHelper.accessor("begin_end", {
      header: "Begin-End",
      cell: info => {
        const row = info.row.original;
        const editing = !!editingCells[row.segment_id]?.begin_end;
        const beginEndText = row.begin_end || "";
        const seekable = parseBeginEndStart(beginEndText) !== null;
        const displayValue = beginEndText ? (
          <button
            type="button"
            className={`timestamp-link ${seekable ? "" : "disabled"}`}
            onClick={() => seekable && seekToSegmentStart(beginEndText)}
            disabled={!seekable}
            title={seekable ? "Jump video to this timestamp" : "Invalid timestamp"}
          >
            {beginEndText}
          </button>
        ) : (
          <i className="muted">—</i>
        );
        return (
          <EditableCell
            initial={row.begin_end}
            displayValue={displayValue}
            onSave={(v) => save(row.segment_id, { begin_end: v })}
            readOnly={row.isHistory}
            isEditing={editing}
            onStart={() => startEditCell(row.segment_id, "begin_end")}
            onStop={(id) => stopEditCell(id || row.segment_id, "begin_end")}
          />
        );
      }
    }),
    columnHelper.accessor("strategy", {
      header: "Strategy",
      cell: info => {
        const row = info.row.original;
        const editing = !!editingCells[row.segment_id]?.strategy;
        return (
          <EditableCell
            initial={row.strategy}
            onSave={(v) => save(row.segment_id, { strategy: v })}
            type="strategy"
            readOnly={row.isHistory}
            isEditing={editing}
            onStart={() => startEditCell(row.segment_id, "strategy")}
            onStop={(id) => stopEditCell(id || row.segment_id, "strategy")}
          />
        );
      }
    }),
    columnHelper.accessor("fidelity_score", {
      header: "Score",
      cell: info => {
        const row = info.row.original;
        const editing = !!editingCells[row.segment_id]?.fidelity_score;
        return (
          <EditableCell
            initial={row.fidelity_score}
            onSave={(v) => save(row.segment_id, { fidelity_score: v })}
            type="score"
            readOnly={row.isHistory}
            isEditing={editing}
            onStart={() => startEditCell(row.segment_id, "fidelity_score")}
            onStop={(id) => stopEditCell(id || row.segment_id, "fidelity_score")}
          />
        );
      }
    }),
    columnHelper.accessor("ai_reasoning", {
      header: "Reasoning",
      cell: info => {
        const row = info.row.original;
        const editing = !!editingCells[row.segment_id]?.ai_reasoning;
        return (
          <EditableCell
            initial={row.ai_reasoning}
            onSave={(v) => save(row.segment_id, { ai_reasoning: v })}
            readOnly={row.isHistory}
            isEditing={editing}
            onStart={() => startEditCell(row.segment_id, "ai_reasoning")}
            onStop={(id) => stopEditCell(id || row.segment_id, "ai_reasoning")}
          />
        );
      }
    }),
    columnHelper.accessor("admin_edited", {
    header: "Edited",
    cell: info => (
      info.getValue() ? (
        <span className="edited-tag">YES</span>
      ) : (
        <span className="muted">—</span>
      )
    )
  }),
    columnHelper.display({
    id: "actions",
    header: " ",
    cell: ({ row }) => {
      const isHistory = row.original?.isHistory;
      const isDeleted = row.original?.is_deleted;
      if (isHistory) {
        if (row.original.historyAction === "restore") {
          return <span className="muted">—</span>;
        }
        const showDeleted = isDeleted || row.original.historyAction === "delete";
        return (
          <button
            className={`icon-button restore ${showDeleted ? "restore-danger" : ""}`}
            onClick={async () => {
              try {
                if (row.original.historyId) {
                  await axios.post(
                    `${API}/api/admin/analysis/history/${row.original.historyId}/restore`,
                    {},
                    { headers: { Authorization: `Bearer ${session.access_token}` } }
                  );
                } else {
                  await axios.post(
                    `${API}/api/admin/analysis/${row.original.segment_id}/restore`,
                    {},
                    { headers: { Authorization: `Bearer ${session.access_token}` } }
                  );
                }
                await fetchRows();
              } catch (err) {
                console.error("Restore failed:", err);
                alert("Restore failed.");
              }
            }}
            title="Restore this version"
          >
            Restore
          </button>
        );
      }
      return (
        <div className="row-actions">
          <button
            className="icon-button delete"
            onClick={() => handleRowDelete(row.original)}
            title="Delete row"
          >
            <FaTrash size={16} style={{ fill: "#aa0000" }} />
          </button>
        </div>
      );
    },
  }),
    // Columns depend on editing state and current draft values.
  ], [handleRowDelete, fetchRows, session.access_token, save, editingCells, startEditCell, stopEditCell, seekToSegmentStart]);

  const pageSize     = 20;
  const [page, setPg] = useState(0);

  const sessions = useMemo(() => {
    const map = new Map();
    const filteredRows = rows.filter(r => {
      const label = getStudyLabel(r.created_at);
      if (studyFilter === "study1") return label === "User Study 1";
      if (studyFilter === "study2") return label === "User Study 2";
      return true;
    });
    filteredRows.forEach(r => {
      if (!map.has(r.session_id)) map.set(r.session_id, r);
    });
    return Array.from(map.values());
  }, [rows, studyFilter]);

  const reliabilitySessions = useMemo(() => {
    return sessions.filter(s => {
      const label = getStudyLabel(s.created_at);
      return label === "User Study 2" && !!s.object_key;
    });
  }, [sessions]);

  const pagedSessions = sessions.slice(page*pageSize, (page+1)*pageSize);
  const usersList = useMemo(() => {
    const map = new Map();
    sessions.forEach(s => {
      if (!s.user_id) return;
      const existing = map.get(s.user_id) || {
        user_id: s.user_id,
        user_name: s.user_name,
        user_email: s.user_email,
        sessions: [],
        latest: null,
      };
      existing.sessions.push(s);
      const ts = s.created_at ? new Date(s.created_at).getTime() : 0;
      if (!existing.latest || ts > existing.latest) {
        existing.latest = ts;
      }
      map.set(s.user_id, existing);
    });
    return Array.from(map.values()).sort((a, b) => (b.latest || 0) - (a.latest || 0));
  }, [sessions]);

  const study2StatsUsers = useMemo(() => {
    const map = new Map();
    sessions.forEach((s) => {
      if (!s.user_id) return;
      if (getStudyLabel(s.created_at) !== "User Study 2") return;

      const existing = map.get(s.user_id) || {
        user_id: s.user_id,
        user_name: s.user_name,
        user_email: s.user_email,
        sessions: [],
        latest: null,
      };
      existing.sessions.push(s);
      const ts = s.created_at ? new Date(s.created_at).getTime() : 0;
      if (!existing.latest || ts > existing.latest) {
        existing.latest = ts;
      }
      map.set(s.user_id, existing);
    });

    return Array.from(map.values()).sort((a, b) => {
      const aName = String(a.user_name || "").toLowerCase();
      const bName = String(b.user_name || "").toLowerCase();
      return aName.localeCompare(bName) || ((b.latest || 0) - (a.latest || 0));
    });
  }, [sessions]);

  const reliabilityUsersList = useMemo(() => {
    const map = new Map();
    reliabilitySessions.forEach(s => {
      if (!s.user_id) return;
      const existing = map.get(s.user_id) || {
        user_id: s.user_id,
        user_name: s.user_name,
        user_email: s.user_email,
        sessions: [],
        latest: null,
      };
      existing.sessions.push(s);
      const ts = s.created_at ? new Date(s.created_at).getTime() : 0;
      if (!existing.latest || ts > existing.latest) {
        existing.latest = ts;
      }
      map.set(s.user_id, existing);
    });
    return Array.from(map.values()).sort((a, b) => (b.latest || 0) - (a.latest || 0));
  }, [reliabilitySessions]);

  const studyCounts = useMemo(() => {
    const excluded = new Set([
      "samarpra@buffalo.edu",
      "imfoobar42@gmail.com",
      "samar.singh.saini@gmail.com",
      "qingxiao@buffalo.edu",
    ]);

    let study1 = 0,
      study2 = 0,
      total = 0;
    sessions.forEach((s) => {
      const email = (s.user_email || "").toLowerCase();
      if (excluded.has(email)) return;
      total++;
      const label = getStudyLabel(s.created_at);
      if (label === "User Study 1") {
        study1++;
      } else {
        study2++;
      }
    });
    return { study1, study2, total };
  }, [sessions]);

  const parseSeconds = (beginEnd) => {
    if (!beginEnd || typeof beginEnd !== "string") return Infinity;
    const parts = beginEnd.split("-")[0]?.trim();
    if (!parts) return Infinity;
    const nums = parts.split(":").map(n => parseFloat(n));
    if (nums.some(n => Number.isNaN(n))) return Infinity;
    if (nums.length === 3) {
      const [h, m, s] = nums;
      return h * 3600 + m * 60 + s;
    }
    if (nums.length === 2) {
      const [m, s] = nums;
      return m * 60 + s;
    }
    return Infinity;
  };

  const selectedRows = useMemo(() => {
    const filtered = rows.filter(r => r.session_id === selectedId);
    return filtered.slice().sort((a, b) => {
      const aNewBlank = !!a.isNew && !a.begin_end;
      const bNewBlank = !!b.isNew && !b.begin_end;
      if (aNewBlank !== bNewBlank) return aNewBlank ? -1 : 1;
      const pa = parseSeconds(a.begin_end);
      const pb = parseSeconds(b.begin_end);
      if (pa !== pb) return pa - pb;
      return 0;
    });
  }, [rows, selectedId]);

  const adminUploadUsers = useMemo(() => {
    if (adminUsers.length) return adminUsers;
    return usersList.map((user) => ({
      id: user.user_id,
      name: user.user_name,
      email: user.user_email,
    }));
  }, [adminUsers, usersList]);

  const activeStatsSessionId = statsSessionId || selectedId;

  const statsRows = useMemo(() => {
    if (!activeStatsSessionId) return [];
    const filtered = rows.filter(r => r.session_id === activeStatsSessionId);
    return filtered.slice().sort((a, b) => {
      const aNewBlank = !!a.isNew && !a.begin_end;
      const bNewBlank = !!b.isNew && !b.begin_end;
      if (aNewBlank !== bNewBlank) return aNewBlank ? -1 : 1;
      const pa = parseSeconds(a.begin_end);
      const pb = parseSeconds(b.begin_end);
      if (pa !== pb) return pa - pb;
      return 0;
    });
  }, [rows, activeStatsSessionId]);

  const statsVideoKey = useMemo(() => {
    const withVideo = statsRows.find((r) => r.object_key);
    return withVideo?.object_key || null;
  }, [statsRows]);

  useEffect(() => {
    if (!showStats || !activeStatsSessionId || !statsVideoKey) return;

    const cachedDuration = Number(videoDurations[activeStatsSessionId]);
    if (Number.isFinite(cachedDuration) && cachedDuration > 0) return;

    const safeKey = encodeURIComponent(statsVideoKey);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = `${API}/api/video/${safeKey}`;

    let cancelled = false;

    const onLoadedMetadata = () => {
      if (cancelled) return;
      const duration = video.duration;
      if (Number.isFinite(duration) && duration > 0) {
        setVideoDurations((prev) => ({
          ...prev,
          [activeStatsSessionId]: duration,
        }));
      }
    };

    const onError = () => {
      // keep as-is; UI will continue showing "—" when metadata is unavailable
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("error", onError);
    video.load();

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
      video.src = "";
    };
  }, [showStats, activeStatsSessionId, statsVideoKey, videoDurations]);

  const sessionStats = useMemo(() => {
    const cleaned = statsRows
      .filter((r) => !r.isNew)
      .map((r) => ({
        strategyKey: normalizeStrategyKey(r.strategy),
        score: Number(r.fidelity_score),
        hasChildResponse: hasChildResponseValue(r.child_response),
      }));

    const validStrategyRows = cleaned.filter((r) => !!r.strategyKey);
    const currentDuration = Number(videoDurations[activeStatsSessionId]);
    const sessionMinutes =
      Number.isFinite(currentDuration) && currentDuration > 0
        ? currentDuration / 60
        : null;

    const fidelityFor = (predicate) => {
      const subset = validStrategyRows.filter(
        (r) => predicate(r.strategyKey) && Number.isFinite(r.score) && r.score > 0
      );
      const sumScores = subset.reduce((acc, r) => acc + r.score, 0);
      const sumFours = subset.reduce((acc, r) => acc + (r.score === 4 ? 4 : 0), 0);
      const percent = sumScores > 0 ? (sumFours / sumScores) * 100 : null;
      return { sumScores, sumFours, percent, count: subset.length };
    };

    const rateFor = (predicate) => {
      const count = validStrategyRows.filter((r) => predicate(r.strategyKey)).length;
      const rate = sessionMinutes && sessionMinutes > 0 ? count / sessionMinutes : null;
      return { count, rate };
    };

    const childResponseRateFor = (predicate) => {
      const opportunities = validStrategyRows.filter(
        (r) =>
          predicate(r.strategyKey) &&
          Number.isFinite(r.score) &&
          isParentProvidedOpportunityScore(r.score)
      );
      const responses = opportunities.filter((r) => r.hasChildResponse).length;
      const percent =
        opportunities.length > 0 ? (responses / opportunities.length) * 100 : null;
      return { responses, opportunities: opportunities.length, percent };
    };

    const isAll = () => true;
    const isTimeDelay = (k) => k === "time_delay";
    const isModelingOrMand = (k) => k === "modeling" || k === "mand_model";

    return {
      sessionMinutes,
      totalStrategyRows: validStrategyRows.length,
      fidelityAll: fidelityFor(isAll),
      fidelityTimeDelay: fidelityFor(isTimeDelay),
      fidelityModelingMand: fidelityFor(isModelingOrMand),
      rateAll: rateFor(isAll),
      rateTimeDelay: rateFor(isTimeDelay),
      rateModelingMand: rateFor(isModelingOrMand),
      childRateAll: childResponseRateFor(isAll),
      childRateTimeDelay: childResponseRateFor(isTimeDelay),
      childRateModelingMand: childResponseRateFor(isModelingOrMand),
    };
  }, [statsRows, activeStatsSessionId, videoDurations]);

  const formatPercent = (value) =>
    Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";
  const formatRate = (value) =>
    Number.isFinite(value) ? `${value.toFixed(2)} / min` : "—";
  const isOverallStatsBrowser = statsBrowseMode && !statsUserId;

  const selectedUserIdForStats = useMemo(() => {
    if (statsUserId) return statsUserId;
    if (statsBrowseMode) return null;
    if (statsRows[0]?.user_id) return statsRows[0].user_id;
    const fallback = rows.find((r) => r.session_id === activeStatsSessionId);
    return fallback?.user_id || null;
  }, [statsUserId, statsBrowseMode, statsRows, rows, activeStatsSessionId]);

  const selectedStatsUser = useMemo(
    () => study2StatsUsers.find((user) => user.user_id === selectedUserIdForStats) || null,
    [study2StatsUsers, selectedUserIdForStats]
  );

  const userStudy2SessionsForStats = useMemo(() => {
    if (!selectedUserIdForStats) return [];
    const sessionMap = new Map();
    rows
      .filter((r) => !r.isNew && r.user_id === selectedUserIdForStats)
      .forEach((r) => {
        if (!r.session_id) return;
        if (getStudyLabel(r.created_at) !== "User Study 2") return;
        if (!sessionMap.has(r.session_id)) {
          sessionMap.set(r.session_id, {
            sessionId: r.session_id,
            createdAt: r.created_at,
            userId: r.user_id,
            userName: r.user_name,
            userEmail: r.user_email,
            activeSegmentIds: new Set(),
          });
        }
        const sessionEntry = sessionMap.get(r.session_id);
        if (r.segment_id) sessionEntry.activeSegmentIds.add(r.segment_id);
      });
    return Array.from(sessionMap.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [rows, selectedUserIdForStats]);

  const allStudy2SessionsForStats = useMemo(() => {
    const sessionMap = new Map();
    rows
      .filter((r) => !r.isNew)
      .forEach((r) => {
        if (!r.session_id || !r.user_id) return;
        if (getStudyLabel(r.created_at) !== "User Study 2") return;
        if (!sessionMap.has(r.session_id)) {
          sessionMap.set(r.session_id, {
            sessionId: r.session_id,
            createdAt: r.created_at,
            userId: r.user_id,
            userName: r.user_name,
            userEmail: r.user_email,
            activeSegmentIds: new Set(),
          });
        }
        const sessionEntry = sessionMap.get(r.session_id);
        if (r.segment_id) sessionEntry.activeSegmentIds.add(r.segment_id);
      });

    return Array.from(sessionMap.values()).sort((a, b) => {
      const userCompare = String(a.userName || "").localeCompare(String(b.userName || ""));
      if (userCompare !== 0) return userCompare;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [rows]);

  const editSessionRowsForStats = useMemo(
    () => (isOverallStatsBrowser ? allStudy2SessionsForStats : userStudy2SessionsForStats),
    [isOverallStatsBrowser, allStudy2SessionsForStats, userStudy2SessionsForStats]
  );

  const includedUserStudy2SessionsForStats = useMemo(
    () =>
      userStudy2SessionsForStats.filter(
        (sessionRow) => statsInclusionBySession[sessionRow.sessionId] !== false
      ),
    [userStudy2SessionsForStats, statsInclusionBySession]
  );

  const includedStatsSessionIndexById = useMemo(() => {
    const indexById = {};
    includedUserStudy2SessionsForStats.forEach((sessionRow, idx) => {
      indexById[sessionRow.sessionId] = idx + 1;
    });
    return indexById;
  }, [includedUserStudy2SessionsForStats]);

  const editsIncludedSessionIndexById = useMemo(() => {
    if (!isOverallStatsBrowser) return includedStatsSessionIndexById;

    const indexById = {};
    const countByUser = new Map();
    editSessionRowsForStats.forEach((sessionRow) => {
      if (statsInclusionBySession[sessionRow.sessionId] === false) return;
      const userKey = sessionRow.userId || sessionRow.userEmail || "unknown";
      const nextIndex = (countByUser.get(userKey) || 0) + 1;
      countByUser.set(userKey, nextIndex);
      indexById[sessionRow.sessionId] = nextIndex;
    });
    return indexById;
  }, [
    isOverallStatsBrowser,
    includedStatsSessionIndexById,
    editSessionRowsForStats,
    statsInclusionBySession,
  ]);

  useEffect(() => {
    if (!showStats || !statsBrowseMode || statsDataMode !== "original") {
      setOriginalStatsLoading(false);
      setOriginalStatsError("");
      return;
    }

    if (!allStudy2SessionsForStats.length) {
      setOriginalStatsRows([]);
      setOriginalStatsLoading(false);
      setOriginalStatsError("");
      return;
    }

    let cancelled = false;

    const fetchOriginalStatsRows = async () => {
      setOriginalStatsLoading(true);
      setOriginalStatsError("");
      try {
        const results = await Promise.all(
          allStudy2SessionsForStats.map(async (sessionRow) => {
            const { data } = await axios.get(`${API}/api/admin/analysis/history`, {
              params: { session_id: sessionRow.sessionId },
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            const activeRows = rows.filter(
              (r) => !r.isNew && r.session_id === sessionRow.sessionId && !!r.segment_id
            );
            return { sessionRow, history: data || [], activeRows };
          })
        );

        if (cancelled) return;

        const originalRows = [];
        results.forEach(({ sessionRow, history, activeRows }) => {
          const bySegment = new Map();
          (history || []).forEach((entry) => {
            if (!entry.segment_id) return;
            if (!bySegment.has(entry.segment_id)) bySegment.set(entry.segment_id, []);
            bySegment.get(entry.segment_id).push(entry);
          });

          const originalSegmentIds = new Set();
          bySegment.forEach((entries, segmentId) => {
            const originalEntry = entries
              .filter((entry) => entry.action === "original")
              .sort((a, b) => {
                const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
                const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
                return ta - tb;
              })[0];

            if (!originalEntry) return;
            originalSegmentIds.add(segmentId);
            originalRows.push({
              ...originalEntry,
              created_at: sessionRow.createdAt,
              session_id: sessionRow.sessionId,
              user_id: sessionRow.userId,
              user_name: sessionRow.userName,
              user_email: sessionRow.userEmail,
              segment_id: segmentId,
              isOriginalStatsRow: true,
            });
          });

          activeRows.forEach((row) => {
            if (originalSegmentIds.has(row.segment_id)) return;
            if (row.admin_edited === true) return;
            originalRows.push({
              ...row,
              created_at: sessionRow.createdAt,
              user_id: sessionRow.userId,
              user_name: sessionRow.userName,
              user_email: sessionRow.userEmail,
              isOriginalStatsRow: true,
            });
          });
        });

        originalRows.sort((a, b) => {
          const ta = parseBeginEndStart(a.begin_end) ?? Number.MAX_SAFE_INTEGER;
          const tb = parseBeginEndStart(b.begin_end) ?? Number.MAX_SAFE_INTEGER;
          if (a.session_id !== b.session_id) {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          }
          return ta - tb;
        });
        setOriginalStatsRows(originalRows);
      } catch (err) {
        console.error("Failed to load original statistics rows:", err);
        if (!cancelled) {
          setOriginalStatsRows([]);
          setOriginalStatsError("Failed to load original model statistics.");
        }
      } finally {
        if (!cancelled) setOriginalStatsLoading(false);
      }
    };

    fetchOriginalStatsRows();
    return () => {
      cancelled = true;
    };
  }, [
    showStats,
    statsBrowseMode,
    statsDataMode,
    allStudy2SessionsForStats,
    rows,
    session.access_token,
  ]);

  const statsAnalysisRows = useMemo(
    () => (statsBrowseMode && statsDataMode === "original" ? originalStatsRows : rows),
    [statsBrowseMode, statsDataMode, originalStatsRows, rows]
  );

  const statsTrendData = useMemo(() => {
    if (!selectedUserIdForStats || !includedUserStudy2SessionsForStats.length) return [];

    const includedSessionIds = new Set(
      includedUserStudy2SessionsForStats.map((sessionRow) => sessionRow.sessionId)
    );

    const bySession = new Map();
    includedUserStudy2SessionsForStats.forEach((sessionRow) => {
      bySession.set(sessionRow.sessionId, {
        sessionId: sessionRow.sessionId,
        createdAt: sessionRow.createdAt,
        userName: sessionRow.userName,
        sumScores: 0,
        sumFours: 0,
        validRows: 0,
        childOpportunities: 0,
        childResponses: 0,
      });
    });

    statsAnalysisRows
      .filter((r) => !r.isNew && r.user_id === selectedUserIdForStats)
      .forEach((r) => {
        if (getStudyLabel(r.created_at) !== "User Study 2") return;
        if (!r.session_id) return;
        if (!includedSessionIds.has(r.session_id)) return;
        const entry = bySession.get(r.session_id);
        const score = Number(r.fidelity_score);

        if (!isValidScoredStrategyRow(r)) return;

        entry.validRows += 1;
        entry.sumScores += score;
        if (score === 4) entry.sumFours += 4;
        if (isParentProvidedOpportunityScore(score)) {
          entry.childOpportunities += 1;
          if (hasChildResponseValue(r.child_response)) {
            entry.childResponses += 1;
          }
        }
      });

    return includedUserStudy2SessionsForStats.map((sessionRow, idx) => {
        const s = bySession.get(sessionRow.sessionId) || sessionRow;
        const hasMinimumRows = (s.validRows || 0) >= 3;
        const parentOverallPercent =
          hasMinimumRows && s.sumScores > 0 ? (s.sumFours / s.sumScores) * 100 : 0;
        const childOverallPercent =
          hasMinimumRows && s.childOpportunities > 0
            ? (s.childResponses / s.childOpportunities) * 100
            : 0;
        const combinedOverallPercent = (parentOverallPercent + childOverallPercent) / 2;

        return {
          ...s,
          index: includedStatsSessionIndexById[s.sessionId] || idx + 1,
          validRows: s.validRows || 0,
          parentOverallPercent,
          childOverallPercent,
          combinedOverallPercent,
        };
      });
  }, [
    statsAnalysisRows,
    selectedUserIdForStats,
    includedUserStudy2SessionsForStats,
    includedStatsSessionIndexById,
  ]);

  const statsTrendBySessionId = useMemo(() => {
    const next = new Map();
    statsTrendData.forEach((row) => {
      next.set(row.sessionId, row);
    });
    return next;
  }, [statsTrendData]);

  const statsGraphTableRows = useMemo(
    () =>
      userStudy2SessionsForStats.map((sessionRow) => {
        const metrics = statsTrendBySessionId.get(sessionRow.sessionId);
        const isIncluded = statsInclusionBySession[sessionRow.sessionId] !== false;
        return {
          ...sessionRow,
          isIncluded,
          index: isIncluded ? includedStatsSessionIndexById[sessionRow.sessionId] || null : null,
          validRows: metrics?.validRows ?? 0,
          parentOverallPercent: metrics?.parentOverallPercent ?? null,
          childOverallPercent: metrics?.childOverallPercent ?? null,
          combinedOverallPercent: metrics?.combinedOverallPercent ?? null,
        };
      }),
    [
      userStudy2SessionsForStats,
      statsTrendBySessionId,
      statsInclusionBySession,
      includedStatsSessionIndexById,
    ]
  );

  const childResponsePhaseCounts = useMemo(() => {
    if (!selectedUserIdForStats) {
      return {
        rows: [],
        totalsByPhase: {},
        unassignedSessionsCount: 0,
        includedSessionsCount: 0,
      };
    }

    const includedSessionIds = new Set(
      includedUserStudy2SessionsForStats.map((sessionRow) => sessionRow.sessionId)
    );
    const countsByLabel = new Map();
    const totalsByPhase = GRAPH_PHASES.reduce((acc, phase) => {
      acc[phase.id] = 0;
      return acc;
    }, {});

    let unassignedSessionsCount = 0;
    includedUserStudy2SessionsForStats.forEach((sessionRow) => {
      const phaseId = graphPhaseBySession[sessionRow.sessionId];
      if (!GRAPH_PHASES.some((phase) => phase.id === phaseId)) {
        unassignedSessionsCount += 1;
      }
    });

    statsAnalysisRows
      .filter((r) => !r.isNew && r.user_id === selectedUserIdForStats)
      .forEach((r) => {
        if (getStudyLabel(r.created_at) !== "User Study 2") return;
        if (!r.session_id || !includedSessionIds.has(r.session_id)) return;

        const phaseId = graphPhaseBySession[r.session_id];
        if (!GRAPH_PHASES.some((phase) => phase.id === phaseId)) return;

        const responseLabel = normalizeChildResponseLabel(r.child_response);
        if (!responseLabel) return;

        if (!countsByLabel.has(responseLabel)) {
          countsByLabel.set(
            responseLabel,
            GRAPH_PHASES.reduce((acc, phase) => {
              acc[phase.id] = 0;
              return acc;
            }, {})
          );
        }

        countsByLabel.get(responseLabel)[phaseId] += 1;
        totalsByPhase[phaseId] += 1;
      });

    const preferredOrder = new Map(
      CHILD_RESPONSE_OPTIONS.map((option, index) => [option.toLowerCase(), index])
    );

    const rows = Array.from(countsByLabel.entries())
      .map(([label, byPhase]) => {
        const total = GRAPH_PHASES.reduce((sum, phase) => sum + (byPhase[phase.id] || 0), 0);
        return { label, byPhase, total };
      })
      .sort((a, b) => {
        const aIndex = preferredOrder.get(a.label.toLowerCase());
        const bIndex = preferredOrder.get(b.label.toLowerCase());
        const aHasPreferred = Number.isInteger(aIndex);
        const bHasPreferred = Number.isInteger(bIndex);
        if (aHasPreferred && bHasPreferred) return aIndex - bIndex;
        if (aHasPreferred) return -1;
        if (bHasPreferred) return 1;
        return a.label.localeCompare(b.label);
      });

    return {
      rows,
      totalsByPhase,
      unassignedSessionsCount,
      includedSessionsCount: includedUserStudy2SessionsForStats.length,
    };
  }, [
    selectedUserIdForStats,
    includedUserStudy2SessionsForStats,
    graphPhaseBySession,
    statsAnalysisRows,
  ]);

  useEffect(() => {
    setGraphPhaseBySession((prev) => {
      const next = {};
      let changed = false;
      userStudy2SessionsForStats.forEach((sessionRow) => {
        if (prev[sessionRow.sessionId]) {
          next[sessionRow.sessionId] = prev[sessionRow.sessionId];
        }
      });
      if (Object.keys(prev).length !== Object.keys(next).length) {
        changed = true;
      }
      if (!changed) return prev;
      return next;
    });
  }, [userStudy2SessionsForStats]);

  const loadGraphPhaseAssignments = useCallback(async () => {
    if (!selectedUserIdForStats) {
      setGraphPhaseBySession({});
      setGraphPhaseError("");
      setGraphPhaseLoading(false);
      return;
    }
    setGraphPhaseLoading(true);
    setGraphPhaseError("");
    try {
      const { data } = await axios.get(`${API}/api/admin/session-phases`, {
        params: { user_id: selectedUserIdForStats },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      const next = {};
      (data || []).forEach((row) => {
        const phase = String(row?.phase || "").toLowerCase();
        if (row?.session_id && GRAPH_PHASES.some((item) => item.id === phase)) {
          next[row.session_id] = phase;
        }
      });
      setGraphPhaseBySession(next);
    } catch (err) {
      console.error("Failed to load graph phase assignments:", err);
      setGraphPhaseError("Failed to load category assignments.");
    } finally {
      setGraphPhaseLoading(false);
    }
  }, [selectedUserIdForStats, session.access_token]);

  const loadStatsInclusionAssignments = useCallback(async () => {
    if (!statsBrowseMode && !selectedUserIdForStats) {
      setStatsInclusionBySession({});
      setEditsChildInclusionBySession({});
      setStatsInclusionError("");
      setStatsInclusionLoading(false);
      return;
    }
    setStatsInclusionLoading(true);
    setStatsInclusionError("");
    try {
      const params = statsBrowseMode ? undefined : { user_id: selectedUserIdForStats };
      const { data } = await axios.get(`${API}/api/admin/session-stats-inclusion`, {
        params,
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      const next = {};
      const nextChild = {};
      (data || []).forEach((row) => {
        if (row?.session_id) {
          next[row.session_id] = row.include_in_stats !== false;
          nextChild[row.session_id] = row.include_child_edits !== false;
        }
      });
      setStatsInclusionBySession(next);
      setEditsChildInclusionBySession(nextChild);
    } catch (err) {
      console.error("Failed to load stats session inclusion:", err);
      setStatsInclusionError("Failed to load session inclusion settings.");
    } finally {
      setStatsInclusionLoading(false);
    }
  }, [statsBrowseMode, selectedUserIdForStats, session.access_token]);

  useEffect(() => {
    if (statsTab !== "graph" && activeGraphPhase) {
      setActiveGraphPhase("");
    }
  }, [statsTab, activeGraphPhase]);

  useEffect(() => {
    if (!showStats || statsTab !== "graph") return;
    loadGraphPhaseAssignments();
  }, [showStats, statsTab, loadGraphPhaseAssignments]);

  useEffect(() => {
    if (!showStats) return;
    loadStatsInclusionAssignments();
  }, [showStats, loadStatsInclusionAssignments]);

  useEffect(() => {
    if (!showStats || statsTab !== "edits") return;
    if (!isOverallStatsBrowser && !selectedUserIdForStats) {
      setEditsStatsRows([]);
      setEditsError("");
      setEditsLoading(false);
      return;
    }
    if (!editSessionRowsForStats.length) {
      setEditsStatsRows([]);
      setEditsError("");
      setEditsLoading(false);
      return;
    }

    let cancelled = false;
    const actionRank = {
      create: 0,
      original: 1,
      update: 2,
      restore: 3,
      delete: 4,
    };
    const buildState = (entry) => ({
      begin_end: entry.begin_end,
      strategy: entry.strategy,
      fidelity_score: entry.fidelity_score,
      ai_reasoning: entry.ai_reasoning,
      child_response: entry.child_response,
      all_sentences: entry.all_sentences,
    });

    const fetchEdits = async () => {
      setEditsLoading(true);
      setEditsError("");
      try {
        const historyResults = await Promise.all(
          editSessionRowsForStats.map(async (s) => {
            const { data } = await axios.get(`${API}/api/admin/analysis/history`, {
              params: { session_id: s.sessionId },
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            const activeRows = rows.filter(
              (r) => !r.isNew && r.session_id === s.sessionId && !!r.segment_id
            );
            return { ...s, history: data || [], activeRows };
          })
        );

        if (cancelled) return;

        const computed = historyResults.map((sessionHistory, idx) => {
          const changedByVariable = new Map();
          EDIT_VARIABLES.forEach((v) => changedByVariable.set(v.id, new Set()));

          const bySegment = new Map();
          const historySegmentIds = new Set();
          (sessionHistory.history || []).forEach((h) => {
            if (!h.segment_id) return;
            historySegmentIds.add(h.segment_id);
            if (!bySegment.has(h.segment_id)) bySegment.set(h.segment_id, []);
            bySegment.get(h.segment_id).push(h);
          });

          const activeBySegment = new Map();
          (sessionHistory.activeRows || []).forEach((row) => {
            activeBySegment.set(row.segment_id, row);
          });

          const allSegmentIds = new Set([
            ...Array.from(historySegmentIds),
            ...Array.from(activeBySegment.keys()),
          ]);

          allSegmentIds.forEach((segmentId) => {
            const entries = bySegment.get(segmentId) || [];
            const ordered = entries
              .slice()
              .sort((a, b) => {
                const ta = new Date(a.created_at).getTime();
                const tb = new Date(b.created_at).getTime();
                if (ta !== tb) return ta - tb;
                const ra = actionRank[a.action] ?? 99;
                const rb = actionRank[b.action] ?? 99;
                return ra - rb;
              });

            const originalEntry = ordered.find(
              (entry) => entry.action === "original" || entry.action === "create"
            );
            const baselineAction = originalEntry?.action || null;
            const originalState = originalEntry
              ? buildState(originalEntry)
              : activeBySegment.has(segmentId)
              ? buildState(activeBySegment.get(segmentId))
              : null;

            const currentActive = activeBySegment.get(segmentId);
            const latestEntry = ordered.length ? ordered[ordered.length - 1] : null;
            const isDeleted = !currentActive && latestEntry?.action === "delete";

            if (isDeleted) {
              // Deleting a row is counted as an edit across all tracked variables.
              EDIT_VARIABLES.forEach((variable) => {
                changedByVariable.get(variable.id).add(segmentId);
              });
              return;
            }

            if (!currentActive) return;

            if (baselineAction === "create") {
              // Admin-created rows should be counted as edited.
              EDIT_VARIABLES.forEach((variable) => {
                changedByVariable.get(variable.id).add(segmentId);
              });
              return;
            }

            // Rows created by admins (no original baseline) count as edited.
            if (!originalState) {
              EDIT_VARIABLES.forEach((variable) => {
                changedByVariable.get(variable.id).add(segmentId);
              });
              return;
            }

            const currentState = buildState(currentActive);
            EDIT_VARIABLES.forEach((variable) => {
              const before = normalizeHistoryValue(originalState[variable.historyKey]);
              const after = normalizeHistoryValue(currentState[variable.historyKey]);
              if (before !== after) {
                changedByVariable.get(variable.id).add(segmentId);
              }
            });
          });

          const totalRows = new Set([
            ...(sessionHistory.activeSegmentIds
              ? Array.from(sessionHistory.activeSegmentIds)
              : []),
            ...Array.from(historySegmentIds),
          ]).size;
          const variableStats = {};
          EDIT_VARIABLES.forEach((v) => {
            const count = changedByVariable.get(v.id).size;
            const totalValues = totalRows;
            const percent = totalValues > 0 ? (count / totalValues) * 100 : null;
            variableStats[v.id] = { count, percent, totalValues, included: true };
          });
          const totalEditsAcrossVariables = EDIT_VARIABLES.reduce(
            (acc, variable) => acc + (variableStats[variable.id]?.count || 0),
            0
          );
          const overallTotalValues = EDIT_VARIABLES.reduce(
            (acc, variable) => acc + (variableStats[variable.id]?.totalValues || 0),
            0
          );
          const overallAveragePercent =
            overallTotalValues > 0
              ? (totalEditsAcrossVariables / overallTotalValues) * 100
              : null;

          return {
            sessionId: sessionHistory.sessionId,
            createdAt: sessionHistory.createdAt,
            userId: sessionHistory.userId,
            userName: sessionHistory.userName,
            userEmail: sessionHistory.userEmail,
            totalRows,
            variableStats,
            totalEditsAcrossVariables,
            overallTotalValues,
            overallAveragePercent,
          };
        });

        setEditsStatsRows(computed);
      } catch (err) {
        console.error("Failed to load edit statistics:", err);
        if (!cancelled) {
          setEditsError("Failed to load edit statistics.");
        }
      } finally {
        if (!cancelled) setEditsLoading(false);
      }
    };

    fetchEdits();
    return () => {
      cancelled = true;
    };
  }, [
    showStats,
    statsTab,
    isOverallStatsBrowser,
    selectedUserIdForStats,
    editSessionRowsForStats,
    rows,
    session.access_token,
  ]);

  const editsDisplayRows = useMemo(
    () =>
      editsStatsRows.map((row) => {
        const isIncluded = statsInclusionBySession[row.sessionId] !== false;
        const includeChildEdits = editsChildInclusionBySession[row.sessionId] !== false;
        const variableStats = {};
        EDIT_VARIABLES.forEach((variable) => {
          const baseMetric = row.variableStats[variable.id] || {
            count: 0,
            percent: null,
            totalValues: 0,
            included: true,
          };
          const included = includeChildEdits || !CHILD_EDIT_VARIABLE_IDS.has(variable.id);
          variableStats[variable.id] = included
            ? { ...baseMetric, included: true }
            : { count: 0, percent: null, totalValues: 0, included: false };
        });
        const totalEditsAcrossVariables = EDIT_VARIABLES.reduce(
          (sum, variable) => sum + (variableStats[variable.id]?.count || 0),
          0
        );
        const overallTotalValues = EDIT_VARIABLES.reduce(
          (sum, variable) => sum + (variableStats[variable.id]?.totalValues || 0),
          0
        );
        const overallAveragePercent =
          overallTotalValues > 0
            ? (totalEditsAcrossVariables / overallTotalValues) * 100
            : null;
        return {
          ...row,
          isIncluded,
          includeChildEdits,
          variableStats,
          totalEditsAcrossVariables,
          overallTotalValues,
          overallAveragePercent,
          index: isIncluded ? editsIncludedSessionIndexById[row.sessionId] || null : null,
        };
      }),
    [
      editsStatsRows,
      statsInclusionBySession,
      editsChildInclusionBySession,
      editsIncludedSessionIndexById,
    ]
  );

  const editsColumnAverages = useMemo(() => {
    const includedRows = editsDisplayRows.filter((row) => row.isIncluded);
    if (!includedRows.length) return null;

    const byVariable = {};
    const totalRowsAllSessions = includedRows.reduce(
      (sum, row) => sum + (Number.isFinite(row.totalRows) ? row.totalRows : 0),
      0
    );

    EDIT_VARIABLES.forEach((variable) => {
      const totalEdits = includedRows.reduce(
        (sum, row) => sum + (row.variableStats[variable.id]?.count || 0),
        0
      );
      const totalValues = includedRows.reduce(
        (sum, row) => sum + (row.variableStats[variable.id]?.totalValues || 0),
        0
      );
      byVariable[variable.id] = {
        totalEdits,
        totalValues,
        avgPercent: totalValues > 0 ? (totalEdits / totalValues) * 100 : null,
      };
    });

    const overallTotalEdits = includedRows.reduce(
      (sum, row) => sum + (row.totalEditsAcrossVariables || 0),
      0
    );
    const overallTotalValues = includedRows.reduce(
      (sum, row) => sum + (row.overallTotalValues || 0),
      0
    );

    return {
      sessionsCount: includedRows.length,
      totalRowsAllSessions,
      overallTotalEdits,
      overallTotalValues,
      overallAvgPercent:
        overallTotalValues > 0 ? (overallTotalEdits / overallTotalValues) * 100 : null,
      byVariable,
    };
  }, [editsDisplayRows]);

  const downloadEditsCsv = useCallback(() => {
    const includedRows = editsDisplayRows.filter((row) => row.isIncluded);
    if (!includedRows.length) return;
    const formatCsvPercent = (value) =>
      Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";

    const includeUserColumns = isOverallStatsBrowser;
    const editVariableHeaders = EDIT_VARIABLES.flatMap((variable) =>
      variable.id === "child_response" ? ["Child Edit Data", variable.label] : [variable.label]
    );
    const header = [
      ...(includeUserColumns ? ["User", "Email"] : []),
      "Session",
      "Date",
      "Total Rows",
      "Avg Edits (All)",
      ...editVariableHeaders,
    ];

    const bodyRows = includedRows.map((row) => [
      ...(includeUserColumns ? [row.userName || "Unnamed user", row.userEmail || "—"] : []),
      row.index ? `S${row.index}` : "",
      row.createdAt ? new Date(row.createdAt).toLocaleString() : "—",
      row.totalRows,
      `${formatCsvPercent(row.overallAveragePercent)} (${row.totalEditsAcrossVariables}/${row.overallTotalValues})`,
      ...EDIT_VARIABLES.flatMap((variable) => {
        const metric = row.variableStats[variable.id];
        const value =
          metric.included === false
            ? "Excluded"
            : `${formatCsvPercent(metric.percent)} (${metric.count}/${metric.totalValues})`;
        if (variable.id === "child_response") {
          return [row.includeChildEdits ? "Included" : "Excluded", value];
        }
        return [value];
      }),
    ]);

    if (editsColumnAverages) {
      bodyRows.push([
        ...(includeUserColumns ? ["Average", "—"] : []),
        includeUserColumns ? "" : "Average",
        `Across ${editsColumnAverages.sessionsCount} sessions`,
        editsColumnAverages.totalRowsAllSessions,
        `${formatCsvPercent(editsColumnAverages.overallAvgPercent)} (${editsColumnAverages.overallTotalEdits}/${editsColumnAverages.overallTotalValues})`,
        ...EDIT_VARIABLES.flatMap((variable) => {
          const metric = editsColumnAverages.byVariable[variable.id];
          const value = `${formatCsvPercent(metric.avgPercent)} (${metric.totalEdits}/${metric.totalValues})`;
          return variable.id === "child_response" ? ["—", value] : [value];
        }),
      ]);
    }

    const csvText = [header, ...bodyRows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = isOverallStatsBrowser
      ? "overall_user_study_2_edits_stats.csv"
      : `${sanitizeFilenamePart(
          selectedStatsUser?.user_name || selectedUserIdForStats || "user"
        )}_edits_stats.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    editsDisplayRows,
    editsColumnAverages,
    isOverallStatsBrowser,
    selectedStatsUser,
    selectedUserIdForStats,
  ]);

  const renderEditsStatsTable = ({ showUserColumn = false } = {}) => (
    <div className="stats-edits-wrap">
      {showUserColumn && (
        <div className="stats-overall-header">
          <div>
            <h4>Overall Edits</h4>
            <p>All included User Study 2 sessions across all participants.</p>
          </div>
        </div>
      )}
      <div className="stats-download-row">
        <button
          className="notify-button secondary stats-download-graph-button"
          onClick={downloadEditsCsv}
          disabled={!editsDisplayRows.some((row) => row.isIncluded)}
        >
          <FiDownload size={15} />
          Download Edits CSV
        </button>
      </div>
      <div className="stats-inclusion-note">
        {showUserColumn
          ? "This table includes all User Study 2 sessions. Uncheck Use to exclude a session from edit averages and numbering; uncheck Child to exclude child response/transcript edit data from the edit averages."
          : "Uncheck Use to exclude a session from edit averages and numbering; uncheck Child to exclude child response/transcript edit data from the edit averages."}
      </div>
      {statsInclusionLoading && <p className="muted">Loading session inclusion settings…</p>}
      {statsInclusionError && <p className="muted">{statsInclusionError}</p>}
      {editsLoading ? (
        <p className="muted">Loading edit statistics…</p>
      ) : editsError ? (
        <p className="muted">{editsError}</p>
      ) : editsDisplayRows.length === 0 ? (
        <p className="muted">No User Study 2 sessions available for edit statistics.</p>
      ) : (
        <table className="stats-table stats-edits-table">
          <thead>
            <tr>
              <th>Use</th>
              {showUserColumn && (
                <>
                  <th>User</th>
                  <th>Email</th>
                </>
              )}
              <th>Session</th>
              <th>Date</th>
              <th>Total Rows</th>
              <th>Avg Edits (All)</th>
              {EDIT_VARIABLES.map((variable) => (
                <React.Fragment key={`hdr-wrap-${variable.id}`}>
                  {variable.id === "child_response" && (
                    <th className="stats-session-toggle-cell stats-child-toggle-header">
                      <label
                        className="stats-child-toggle-label"
                        title="Toggle child response/transcript edit data for all included sessions"
                      >
                        <input
                          type="checkbox"
                          className="stats-session-checkbox"
                          checked={
                            editsDisplayRows.some((row) => row.isIncluded) &&
                            editsDisplayRows
                              .filter((row) => row.isIncluded)
                              .every((row) => row.includeChildEdits)
                          }
                          disabled={!editsDisplayRows.some((row) => row.isIncluded)}
                          onChange={(e) =>
                            toggleAllEditsChildSessionsIncluded(e.target.checked)
                          }
                        />
                        <span>Child</span>
                      </label>
                    </th>
                  )}
                  <th>{variable.label}</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {editsDisplayRows.map((row) => (
              <tr
                key={`edits-${row.sessionId}`}
                className={[
                  row.sessionId === activeStatsSessionId ? "stats-row-active" : "",
                  !row.isIncluded ? "stats-row-excluded" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td className="stats-session-toggle-cell">
                  <input
                    type="checkbox"
                    className="stats-session-checkbox"
                    checked={row.isIncluded}
                    onChange={(e) => toggleStatsSessionIncluded(row.sessionId, e.target.checked)}
                  />
                </td>
                {showUserColumn && (
                  <>
                    <td>{row.userName || "Unnamed user"}</td>
                    <td>{row.userEmail || "—"}</td>
                  </>
                )}
                <td>
                  {row.index ? (
                    `S${row.index}`
                  ) : (
                    <span className="stats-session-excluded">Excluded</span>
                  )}
                </td>
                <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                <td>{row.totalRows}</td>
                <td>
                  {`${formatPercent(row.overallAveragePercent)} (${row.totalEditsAcrossVariables}/${
                    row.overallTotalValues
                  })`}
                </td>
                {EDIT_VARIABLES.map((variable) => {
                  const metric = row.variableStats[variable.id];
                  return (
                    <React.Fragment key={`cell-wrap-${row.sessionId}-${variable.id}`}>
                      {variable.id === "child_response" && (
                        <td className="stats-session-toggle-cell">
                          <input
                            type="checkbox"
                            className="stats-session-checkbox"
                            checked={row.includeChildEdits}
                            disabled={!row.isIncluded}
                            onChange={(e) =>
                              toggleEditsChildSessionIncluded(row.sessionId, e.target.checked)
                            }
                          />
                        </td>
                      )}
                      <td>
                        {metric.included === false ? (
                          <span className="stats-session-excluded">Excluded</span>
                        ) : (
                          `${formatPercent(metric.percent)} (${metric.count}/${metric.totalValues})`
                        )}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {editsColumnAverages && (
            <tfoot>
              <tr className="stats-summary-row">
                <td>—</td>
                {showUserColumn && (
                  <>
                    <td>Average</td>
                    <td>—</td>
                  </>
                )}
                <td>{showUserColumn ? "—" : "Average"}</td>
                <td>{`Across ${editsColumnAverages.sessionsCount} sessions`}</td>
                <td>{editsColumnAverages.totalRowsAllSessions}</td>
                <td>
                  {`${formatPercent(editsColumnAverages.overallAvgPercent)} (${
                    editsColumnAverages.overallTotalEdits
                  }/${editsColumnAverages.overallTotalValues})`}
                </td>
                {EDIT_VARIABLES.map((variable) => {
                  const metric = editsColumnAverages.byVariable[variable.id];
                  return (
                    <React.Fragment key={`avg-wrap-${variable.id}`}>
                      {variable.id === "child_response" && <td>—</td>}
                      <td>
                        {`${formatPercent(metric.avgPercent)} (${metric.totalEdits}/${
                          metric.totalValues
                        })`}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );

  const renderChildResponsePhaseTable = () => (
    <div className="stats-edits-wrap">
      <div className="stats-overall-header">
        <div>
          <h4>Child Response Counts by Category</h4>
          <p>
            Summed across included User Study 2 sessions for this user using the current{" "}
            {statsBrowseMode ? `${statsDataMode} ` : ""}
            data.
          </p>
        </div>
      </div>
      <div className="stats-inclusion-note">
        Only sessions still checked in the session list are counted here. Sessions without a
        category assignment are excluded from the phase totals.
      </div>
      {childResponsePhaseCounts.includedSessionsCount === 0 ? (
        <p className="muted">No included sessions are currently selected for this user.</p>
      ) : childResponsePhaseCounts.rows.length === 0 ? (
        <p className="muted">No child response values are available for the included sessions.</p>
      ) : (
        <>
          <table className="stats-table stats-phase-table">
            <thead>
              <tr>
                <th>Child Response</th>
                {GRAPH_PHASES.map((phase) => (
                  <th key={`child-phase-hdr-${phase.id}`}>{phase.label}</th>
                ))}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {childResponsePhaseCounts.rows.map((row) => (
                <tr key={`child-phase-row-${row.label}`}>
                  <td>{row.label}</td>
                  {GRAPH_PHASES.map((phase) => (
                    <td key={`child-phase-cell-${row.label}-${phase.id}`}>
                      {row.byPhase[phase.id] || 0}
                    </td>
                  ))}
                  <td>{row.total}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="stats-summary-row">
                <td>Total</td>
                {GRAPH_PHASES.map((phase) => (
                  <td key={`child-phase-total-${phase.id}`}>
                    {childResponsePhaseCounts.totalsByPhase[phase.id] || 0}
                  </td>
                ))}
                <td>
                  {GRAPH_PHASES.reduce(
                    (sum, phase) =>
                      sum + (childResponsePhaseCounts.totalsByPhase[phase.id] || 0),
                    0
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
          {childResponsePhaseCounts.unassignedSessionsCount > 0 && (
            <p className="muted">
              {childResponsePhaseCounts.unassignedSessionsCount} included session
              {childResponsePhaseCounts.unassignedSessionsCount === 1 ? "" : "s"} are currently
              unassigned and not counted in the phase totals.
            </p>
          )}
        </>
      )}
    </div>
  );

  const trendChart = useMemo(() => {
    const width = 760;
    const height = 330;
    const padLeft = 56;
    const padRight = 20;
    const padTop = 16;
    const padBottom = 54;
    const plotW = width - padLeft - padRight;
    const plotH = height - padTop - padBottom;
    const count = statsTrendData.length;
    const divisor = count > 1 ? count - 1 : 1;

    const xAt = (i) => padLeft + (i / divisor) * plotW;
    const yAt = (value) => padTop + ((100 - value) / 100) * plotH;

    const buildPath = (key) => {
      let d = "";
      let started = false;
      statsTrendData.forEach((row, i) => {
        const value = row[key];
        if (!Number.isFinite(value)) {
          started = false;
          return;
        }
        const x = xAt(i);
        const y = yAt(value);
        if (!started) {
          d += `M ${x} ${y}`;
          started = true;
        } else {
          d += ` L ${x} ${y}`;
        }
      });
      return d;
    };

    const parentPath = buildPath("parentOverallPercent");
    const childPath = buildPath("childOverallPercent");
    const yTicks = [0, 20, 40, 60, 80, 100];

    return {
      width,
      height,
      padLeft,
      padTop,
      padBottom,
      plotW,
      plotH,
      yTicks,
      xAt,
      yAt,
      parentPath,
      childPath,
    };
  }, [statsTrendData]);
  const activeTrendIndex = useMemo(
    () => statsTrendData.findIndex((row) => row.sessionId === activeStatsSessionId),
    [statsTrendData, activeStatsSessionId]
  );
  const graphAverages = useMemo(() => {
    const avg = (values) => {
      const numeric = values.filter((value) => Number.isFinite(value));
      if (!numeric.length) return null;
      return numeric.reduce((sum, value) => sum + value, 0) / numeric.length;
    };
    const summarize = (rowsSubset) => ({
      sessionsCount: rowsSubset.length,
      parentAvg: avg(rowsSubset.map((row) => row.parentOverallPercent)),
      childAvg: avg(rowsSubset.map((row) => row.childOverallPercent)),
      combinedAvg: avg(rowsSubset.map((row) => row.combinedOverallPercent)),
    });

    const byPhase = {};
    GRAPH_PHASES.forEach((phase) => {
      byPhase[phase.id] = summarize(
        statsTrendData.filter((row) => graphPhaseBySession[row.sessionId] === phase.id)
      );
    });

    return {
      overall: summarize(statsTrendData),
      byPhase,
    };
  }, [statsTrendData, graphPhaseBySession]);

  const overallStudy2TrendData = useMemo(() => {
    return study2StatsUsers.map((user) => {
      const bySession = new Map();
      const baseSessions = user.sessions
        .filter((sessionRow) => statsInclusionBySession[sessionRow.session_id] !== false)
        .slice()
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      baseSessions.forEach((sessionRow) => {
        bySession.set(sessionRow.session_id, {
          sessionId: sessionRow.session_id,
          createdAt: sessionRow.created_at,
          sumScores: 0,
          sumFours: 0,
          validRows: 0,
          childOpportunities: 0,
          childResponses: 0,
        });
      });

      statsAnalysisRows
        .filter((r) => !r.isNew && r.user_id === user.user_id)
        .forEach((r) => {
          if (getStudyLabel(r.created_at) !== "User Study 2") return;
          if (!r.session_id) return;
          if (statsInclusionBySession[r.session_id] === false) return;
          if (!bySession.has(r.session_id)) return;

          const entry = bySession.get(r.session_id);
          const score = Number(r.fidelity_score);

          if (!isValidScoredStrategyRow(r)) return;

          entry.validRows += 1;
          entry.sumScores += score;
          if (score === 4) entry.sumFours += 4;
          if (isParentProvidedOpportunityScore(score)) {
            entry.childOpportunities += 1;
            if (hasChildResponseValue(r.child_response)) {
              entry.childResponses += 1;
            }
          }
        });

      const sessions = baseSessions
        .map((baseSession, idx) => {
          const sessionRow = bySession.get(baseSession.session_id) || {
            sessionId: baseSession.session_id,
            createdAt: baseSession.created_at,
            validRows: 0,
          };
          const hasMinimumRows = (sessionRow.validRows || 0) >= 3;
          const parentOverallPercent =
            hasMinimumRows && sessionRow.sumScores > 0
              ? (sessionRow.sumFours / sessionRow.sumScores) * 100
              : 0;
          const childOverallPercent =
            hasMinimumRows && sessionRow.childOpportunities > 0
              ? (sessionRow.childResponses / sessionRow.childOpportunities) * 100
              : 0;
          const combinedOverallPercent = (parentOverallPercent + childOverallPercent) / 2;

          return {
            ...sessionRow,
            index: idx + 1,
            validRows: sessionRow.validRows || 0,
            parentOverallPercent,
            childOverallPercent,
            combinedOverallPercent,
          };
        });

      return {
        userId: user.user_id,
        userName: user.user_name || user.user_email || "Unnamed user",
        userEmail: user.user_email || "",
        sessions,
      };
    });
  }, [study2StatsUsers, statsAnalysisRows, statsInclusionBySession]);

  const overallStudy2MaxSessions = useMemo(
    () => overallStudy2TrendData.reduce((max, user) => Math.max(max, user.sessions.length), 0),
    [overallStudy2TrendData]
  );

  const overallStudy2Chart = useMemo(() => {
    const width = 860;
    const bandPlotHeight = 92;
    const bandGap = 12;
    const bandHeight = bandPlotHeight + bandGap;
    const padLeft = 126;
    const padRight = 22;
    const padTop = 16;
    const padBottom = 42;
    const plotW = width - padLeft - padRight;
    const userCount = Math.max(overallStudy2TrendData.length, 1);
    const height = padTop + userCount * bandPlotHeight + Math.max(0, userCount - 1) * bandGap + padBottom;
    const count = overallStudy2MaxSessions;
    const divisor = count > 1 ? count - 1 : 1;

    const xAt = (index) =>
      count <= 1 ? padLeft + plotW / 2 : padLeft + (index / divisor) * plotW;
    const bandTopAt = (userIndex) => padTop + userIndex * bandHeight;
    const bandBottomAt = (userIndex) => bandTopAt(userIndex) + bandPlotHeight;
    const yAt = (userIndex, value) =>
      bandTopAt(userIndex) + ((100 - value) / 100) * bandPlotHeight;

    return {
      width,
      height,
      padLeft,
      padRight,
      padTop,
      padBottom,
      plotW,
      bandHeight,
      bandPlotHeight,
      bandGap,
      xAt,
      yAt,
      bandTopAt,
      bandBottomAt,
      yTicks: [20, 40, 60, 80, 100],
    };
  }, [overallStudy2TrendData.length, overallStudy2MaxSessions]);

  const downloadStatsGraph = useCallback(() => {
    if (!statsTrendData.length) return;

    const grayscalePhaseFill = {
      baseline: "#f9fafb",
      treatment: "#e5e7eb",
      endline: "#dbdddf",
    };
    const exportWidth = trendChart.width;
    const topOffset = 112;
    const exportHeight = trendChart.height + topOffset;
    const activeIncludedIndex = statsTrendData.findIndex(
      (row) => row.sessionId === activeStatsSessionId
    );
    const formatExportPercent = (value) =>
      Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";
    const title = selectedStatsUser?.user_name
      ? `${selectedStatsUser.user_name} - Performance`
      : "Performance";
    const subtitle = `Included sessions: ${statsTrendData.length} | Parent avg ${formatExportPercent(
      graphAverages.overall.parentAvg
    )} | Child avg ${formatExportPercent(graphAverages.overall.childAvg)}`;
    const shiftY = (y) => y + topOffset;
    const getBandBounds = (index) => {
      const bandWidth =
        statsTrendData.length > 1
          ? trendChart.plotW / (statsTrendData.length - 1)
          : trendChart.plotW;
      const leftRaw =
        statsTrendData.length > 1 ? trendChart.xAt(index) - bandWidth / 2 : trendChart.padLeft;
      const rightRaw =
        statsTrendData.length > 1
          ? trendChart.xAt(index) + bandWidth / 2
          : trendChart.padLeft + trendChart.plotW;
      const left = Math.max(trendChart.padLeft, leftRaw);
      const right = Math.min(trendChart.padLeft + trendChart.plotW, rightRaw);
      return { left, right, width: Math.max(0, right - left) };
    };
    const phaseBands = statsTrendData
      .map((row, i) => {
        const phaseId = graphPhaseBySession[row.sessionId];
        if (!phaseId || !grayscalePhaseFill[phaseId]) return "";
        const { left, width } = getBandBounds(i);
        if (width <= 0) return "";
        return `<rect x="${left}" y="${shiftY(trendChart.padTop)}" width="${width}" height="${trendChart.plotH}" fill="${grayscalePhaseFill[phaseId]}" />`;
      })
      .join("");
    const phaseBlocks = [];
    statsTrendData.forEach((row, i) => {
      const phaseId = graphPhaseBySession[row.sessionId];
      if (!phaseId || !grayscalePhaseFill[phaseId]) return;
      const last = phaseBlocks[phaseBlocks.length - 1];
      if (last && last.phaseId === phaseId && last.endIndex === i - 1) {
        last.endIndex = i;
        return;
      }
      phaseBlocks.push({ phaseId, endIndex: i, startIndex: i });
    });
    const phaseLabels = phaseBlocks
      .map((block) => {
        const phaseMeta = GRAPH_PHASES.find((phase) => phase.id === block.phaseId);
        if (!phaseMeta) return "";
        const startBounds = getBandBounds(block.startIndex);
        const endBounds = getBandBounds(block.endIndex);
        const left = startBounds.left;
        const right = endBounds.right;
        const width = Math.max(0, right - left);
        if (width <= 0) return "";
        const center = left + width / 2;
        const labelY = shiftY(trendChart.padTop) - 14;
        const bracketY = shiftY(trendChart.padTop) - 6;
        return `
          <g>
            <line x1="${left}" x2="${right}" y1="${bracketY}" y2="${bracketY}" stroke="#6b7280" stroke-width="1" />
            <line x1="${left}" x2="${left}" y1="${bracketY}" y2="${bracketY + 4}" stroke="#6b7280" stroke-width="1" />
            <line x1="${right}" x2="${right}" y1="${bracketY}" y2="${bracketY + 4}" stroke="#6b7280" stroke-width="1" />
            <text x="${center}" y="${labelY}" text-anchor="middle" font-size="11" font-weight="600" fill="#374151">${escapeXml(
              phaseMeta.label
            )}</text>
          </g>`;
      })
      .join("");
    const yGridLines = trendChart.yTicks
      .map(
        (tick) => `
          <g>
            <line x1="${trendChart.padLeft}" x2="${trendChart.width - 20}" y1="${shiftY(
              trendChart.yAt(tick)
            )}" y2="${shiftY(trendChart.yAt(tick))}" stroke="#9ca3af" stroke-width="1" />
            <text x="${trendChart.padLeft - 8}" y="${shiftY(
              trendChart.yAt(tick) + 4
            )}" text-anchor="end" font-size="11" fill="#4b5563">${tick}</text>
          </g>`
      )
      .join("");
    const points = statsTrendData
      .map((row, i) => {
        const x = trendChart.xAt(i);
        const parentValue = row.parentOverallPercent;
        const childValue = row.childOverallPercent;
        const isActive = row.sessionId === activeStatsSessionId;
        const sessionLabel = row.index ? `S${row.index}` : "";
        return `
          <g>
            ${
              Number.isFinite(parentValue)
                ? `<circle cx="${x}" cy="${shiftY(
                    trendChart.yAt(parentValue)
                  )}" r="${isActive ? 5.5 : 4}" fill="#1f2937" />`
                : ""
            }
            ${
              Number.isFinite(childValue)
                ? `<circle cx="${x}" cy="${shiftY(
                    trendChart.yAt(childValue)
                  )}" r="${isActive ? 5.5 : 4}" fill="#ffffff" stroke="#4b5563" stroke-width="2" />`
                : ""
            }
            <text x="${x}" y="${shiftY(
              trendChart.height - trendChart.padBottom + 18
            )}" text-anchor="middle" font-size="10" fill="${
              isActive ? "#111827" : "#6b7280"
            }" font-weight="${isActive ? "700" : "400"}">${escapeXml(sessionLabel)}</text>
          </g>`;
      })
      .join("");
    const svgMarkup = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${exportWidth}" height="${exportHeight}" viewBox="0 0 ${exportWidth} ${exportHeight}" role="img" aria-label="${escapeXml(
      title
    )}">
  <rect width="100%" height="100%" fill="#ffffff" />
  <text x="24" y="28" font-size="18" font-weight="700" fill="#111827">${escapeXml(title)}</text>
  <text x="24" y="48" font-size="11.5" fill="#4b5563">${escapeXml(subtitle)}</text>

  <g transform="translate(24, 60)">
    <line x1="0" y1="0" x2="30" y2="0" stroke="#1f2937" stroke-width="2.6" />
    <text x="40" y="4" font-size="12" fill="#374151">Parent Overall Fidelity %</text>
    <line x1="250" y1="0" x2="280" y2="0" stroke="#4b5563" stroke-width="2.6" stroke-dasharray="7 5" />
    <text x="290" y="4" font-size="12" fill="#374151">Child Responsiveness %</text>
  </g>

  <g transform="translate(24, 82)">
    <rect x="0" y="-8" width="24" height="12" fill="#f9fafb" stroke="#9ca3af" stroke-width="0.8" />
    <text x="34" y="1" font-size="12" fill="#374151">Baseline</text>
    <rect x="122" y="-8" width="24" height="12" fill="#e5e7eb" stroke="#9ca3af" stroke-width="0.8" />
    <text x="156" y="1" font-size="12" fill="#374151">Treatment</text>
    <rect x="260" y="-8" width="24" height="12" fill="#d1d5db" stroke="#9ca3af" stroke-width="0.8" />
    <text x="294" y="1" font-size="12" fill="#374151">Follow-Up</text>
  </g>

  ${phaseBands}
  ${phaseLabels}
  ${yGridLines}

  <line x1="${trendChart.padLeft}" x2="${trendChart.padLeft}" y1="${shiftY(
      16
    )}" y2="${shiftY(trendChart.height - trendChart.padBottom)}" stroke="#6b7280" stroke-width="1.2" />
  <line x1="${trendChart.padLeft}" x2="${trendChart.width - 20}" y1="${shiftY(
      trendChart.height - trendChart.padBottom
    )}" y2="${shiftY(trendChart.height - trendChart.padBottom)}" stroke="#6b7280" stroke-width="1.2" />
  ${
    activeIncludedIndex >= 0
      ? `<line x1="${trendChart.xAt(activeIncludedIndex)}" x2="${trendChart.xAt(
          activeIncludedIndex
        )}" y1="${shiftY(16)}" y2="${shiftY(
          trendChart.height - trendChart.padBottom
        )}" stroke="#6b7280" stroke-width="1.2" stroke-dasharray="3 4" />`
      : ""
  }
  ${
    trendChart.parentPath
      ? `<path d="${trendChart.parentPath.replace(/([0-9.]+) ([0-9.]+)/g, (_, x, y) => `${x} ${Number(y) + topOffset}`)}" fill="none" stroke="#1f2937" stroke-width="2.6" />`
      : ""
  }
  ${
    trendChart.childPath
      ? `<path d="${trendChart.childPath.replace(/([0-9.]+) ([0-9.]+)/g, (_, x, y) => `${x} ${Number(y) + topOffset}`)}" fill="none" stroke="#4b5563" stroke-width="2.6" stroke-dasharray="7 5" />`
      : ""
  }
  ${points}
</svg>`;

    const filename = `${sanitizeFilenamePart(
      selectedStatsUser?.user_name || selectedUserIdForStats || "user"
    )}_stats_graph_grayscale.svg`;
    downloadSvgFile(filename, svgMarkup);
  }, [
    statsTrendData,
    trendChart,
    activeStatsSessionId,
    graphPhaseBySession,
    graphAverages.overall.parentAvg,
    graphAverages.overall.childAvg,
    selectedStatsUser,
    selectedUserIdForStats,
  ]);

  // Kept as the shared static export renderer; the current overall Excel export uses native charts.
  // eslint-disable-next-line no-unused-vars
  const buildOverallStudy2GraphSvg = useCallback(() => {
    if (!overallStudy2TrendData.length || !overallStudy2MaxSessions) return null;

    const exportWidth = overallStudy2Chart.width;
    const headerHeight = 90;
    const exportHeight = overallStudy2Chart.height + headerHeight;
    const shiftY = (y) => y + headerHeight;
    const formatExportPercent = (value) =>
      Number.isFinite(value) ? `${value.toFixed(1)}%` : "—";

    const parentValues = overallStudy2TrendData.flatMap((user) =>
      user.sessions.map((sessionRow) => sessionRow.parentOverallPercent)
    );
    const childValues = overallStudy2TrendData.flatMap((user) =>
      user.sessions.map((sessionRow) => sessionRow.childOverallPercent)
    );
    const average = (values) => {
      const valid = values.filter((value) => Number.isFinite(value));
      if (!valid.length) return null;
      return valid.reduce((sum, value) => sum + value, 0) / valid.length;
    };

    const userBands = overallStudy2TrendData
      .map((user, userIndex) => {
        const bandTop = overallStudy2Chart.bandTopAt(userIndex);
        const bandBottom = overallStudy2Chart.bandBottomAt(userIndex);
        const labelLines = splitOverallGraphLabel(user.userName);
        const labelX = 18;
        const labelBaseY =
          shiftY(bandTop + overallStudy2Chart.bandPlotHeight / 2) -
          (labelLines.length - 1) * 7;
        const parentPath = user.sessions.reduce((path, sessionRow, index) => {
          const value = sessionRow.parentOverallPercent;
          if (!Number.isFinite(value)) return path;
          const x = overallStudy2Chart.xAt(index);
          const y = overallStudy2Chart.yAt(userIndex, value);
          return `${path}${path ? " L" : "M"} ${x} ${y}`;
        }, "");
        const childPath = user.sessions.reduce((path, sessionRow, index) => {
          const value = sessionRow.childOverallPercent;
          if (!Number.isFinite(value)) return path;
          const x = overallStudy2Chart.xAt(index);
          const y = overallStudy2Chart.yAt(userIndex, value);
          return `${path}${path ? " L" : "M"} ${x} ${y}`;
        }, "");

        const gridLines = overallStudy2Chart.yTicks
          .map(
            (tick) => `
              <text x="${overallStudy2Chart.padLeft - 8}" y="${shiftY(
                overallStudy2Chart.yAt(userIndex, tick) + 4
              )}" text-anchor="end" font-size="10.5" fill="#4b5563">${tick}</text>`
          )
          .join("");

        const points = user.sessions
          .map((sessionRow, index) => {
            const x = overallStudy2Chart.xAt(index);
            const parentValue = sessionRow.parentOverallPercent;
            const childValue = sessionRow.childOverallPercent;
            return `
              ${
                Number.isFinite(parentValue)
                  ? `<circle cx="${x}" cy="${shiftY(
                      overallStudy2Chart.yAt(userIndex, parentValue)
                    )}" r="3.8" fill="#1f2937" />`
                  : ""
              }
              ${
                Number.isFinite(childValue)
                  ? `<circle cx="${x}" cy="${shiftY(
                      overallStudy2Chart.yAt(userIndex, childValue)
                    )}" r="3.8" fill="#ffffff" stroke="#4b5563" stroke-width="1.8" />`
                  : ""
              }`;
          })
          .join("");

        return `
          <g>
            <rect x="${overallStudy2Chart.padLeft}" y="${shiftY(
              bandTop
            )}" width="${overallStudy2Chart.plotW}" height="${overallStudy2Chart.bandPlotHeight}" fill="#ffffff" stroke="#cbd5e1" stroke-width="1" />
            <text x="${labelX}" y="${labelBaseY}" text-anchor="start" font-size="11.5" font-weight="700" fill="#111827">${labelLines
              .map((line, idx) =>
                `<tspan x="${labelX}" dy="${idx === 0 ? 0 : 14}">${escapeXml(line)}</tspan>`
              )
              .join("")}</text>
            ${gridLines}
            <line x1="${overallStudy2Chart.padLeft}" x2="${overallStudy2Chart.padLeft}" y1="${shiftY(
              bandTop
            )}" y2="${shiftY(bandBottom)}" stroke="#6b7280" stroke-width="1" />
            <line x1="${overallStudy2Chart.padLeft}" x2="${
              overallStudy2Chart.width - overallStudy2Chart.padRight
            }" y1="${shiftY(bandBottom)}" y2="${shiftY(
              bandBottom
            )}" stroke="#6b7280" stroke-width="1" />
            ${parentPath ? `<path d="${parentPath.replace(/([0-9.]+) ([0-9.]+)/g, (_, x, y) => `${x} ${Number(y) + headerHeight}`)}" fill="none" stroke="#1f2937" stroke-width="2.4" />` : ""}
            ${childPath ? `<path d="${childPath.replace(/([0-9.]+) ([0-9.]+)/g, (_, x, y) => `${x} ${Number(y) + headerHeight}`)}" fill="none" stroke="#4b5563" stroke-width="2.4" stroke-dasharray="7 5" />` : ""}
            ${points}
          </g>`;
      })
      .join("");

    const bottomAxisLabels = Array.from({ length: overallStudy2MaxSessions }, (_, index) => {
      const label = `S${index + 1}`;
      return `<text x="${overallStudy2Chart.xAt(index)}" y="${shiftY(
        overallStudy2Chart.height - overallStudy2Chart.padBottom + 20
      )}" text-anchor="middle" font-size="10" fill="#6b7280">${label}</text>`;
    }).join("");

    const svgMarkup = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${exportWidth}" height="${exportHeight}" viewBox="0 0 ${exportWidth} ${exportHeight}" role="img" aria-label="Overall participant performance">
  <rect width="100%" height="100%" fill="#ffffff" />
  <text x="24" y="28" font-size="18" font-weight="700" fill="#111827">Overall Participant Performance</text>
  <text x="24" y="48" font-size="11.5" fill="#4b5563">Users: ${overallStudy2TrendData.length} | Max sessions shown: ${overallStudy2MaxSessions} | Parent avg ${formatExportPercent(
      average(parentValues)
    )} | Child avg ${formatExportPercent(average(childValues))}</text>
  <g transform="translate(24, 72)">
    <line x1="0" y1="0" x2="30" y2="0" stroke="#1f2937" stroke-width="2.6" />
    <text x="40" y="4" font-size="12" fill="#374151">Parent Overall Fidelity %</text>
    <line x1="250" y1="0" x2="280" y2="0" stroke="#4b5563" stroke-width="2.6" stroke-dasharray="7 5" />
    <text x="290" y="4" font-size="12" fill="#374151">Child Responsiveness %</text>
  </g>
  ${userBands}
  ${bottomAxisLabels}
</svg>`;

    return {
      svgMarkup,
      width: exportWidth,
      height: exportHeight,
    };
  }, [overallStudy2TrendData, overallStudy2MaxSessions, overallStudy2Chart]);

  const downloadOverallStudy2Excel = useCallback(() => {
    if (!overallStudy2TrendData.length || !overallStudy2MaxSessions) return;

    const sessionHeaders = Array.from({ length: overallStudy2MaxSessions }, (_, index) => ({
      value: `S${index + 1}`,
      style: 1,
    }));
    const toExcelPercent = (value) =>
      Number.isFinite(value) ? Number(value.toFixed(1)) : null;
    const toExcelSmoothSessionValue = (user, index, key) => {
      if (index >= user.sessions.length) {
        return { error: "#N/A" };
      }
      const value = toExcelPercent(user.sessions[index]?.[key]);
      return value === 0 ? null : value;
    };
    const buildZeroTouchingSegmentRows = (user, key, dashed) => {
      const rows = [];
      for (let index = 0; index < user.sessions.length - 1; index += 1) {
        const leftValue = toExcelPercent(user.sessions[index]?.[key]);
        const rightValue = toExcelPercent(user.sessions[index + 1]?.[key]);
        if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) continue;
        if (leftValue !== 0 && rightValue !== 0) continue;

        rows.push({
          row: [
            user.userName,
            dashed ? "Child Responsiveness % helper" : "Parent Overall Fidelity % helper",
            ...Array.from({ length: overallStudy2MaxSessions }, (_, sessionIndex) => {
              if (sessionIndex >= user.sessions.length) return { error: "#N/A" };
              if (sessionIndex === index) return leftValue;
              if (sessionIndex === index + 1) return rightValue;
              return { error: "#N/A" };
            }),
          ],
          color: dashed ? "4B5563" : "1F2937",
          dashed,
          marker: dashed ? "triangle" : "circle",
        });
      }
      return rows;
    };

    const wideRows = [
      [{ value: "Overall Participant Performance", style: 1 }],
      [
        `Users: ${overallStudy2TrendData.length}`,
        `Max sessions shown: ${overallStudy2MaxSessions}`,
        `Data source: ${statsDataMode === "original" ? "Original model rows" : "Final admin-reviewed rows"}`,
      ],
      [],
      [{ value: "User", style: 1 }, { value: "Measure", style: 1 }, ...sessionHeaders],
    ];
    const dataSheetName = "Overall Graph Data";
    const categoriesRef = excelSheetRef(dataSheetName, 4, 2, 4, overallStudy2MaxSessions + 1);
    const chartSeriesByUser = [];

    overallStudy2TrendData.forEach((user) => {
      const parentRow = wideRows.length + 1;
      wideRows.push([
        user.userName,
        "Parent Overall Fidelity %",
        ...Array.from({ length: overallStudy2MaxSessions }, (_, index) =>
          toExcelSmoothSessionValue(user, index, "parentOverallPercent")
        ),
      ]);
      const childRow = wideRows.length + 1;
      wideRows.push([
        user.userName,
        "Child Responsiveness %",
        ...Array.from({ length: overallStudy2MaxSessions }, (_, index) =>
          toExcelSmoothSessionValue(user, index, "childOverallPercent")
        ),
      ]);

      const extraSeries = [];
      buildZeroTouchingSegmentRows(user, "parentOverallPercent", false).forEach((segment) => {
        const rowNumber = wideRows.length + 1;
        wideRows.push(segment.row);
        extraSeries.push({
          valuesRef: excelSheetRef(
            dataSheetName,
            rowNumber,
            2,
            rowNumber,
            overallStudy2MaxSessions + 1
          ),
          color: segment.color,
          dashed: segment.dashed,
          marker: segment.marker,
        });
      });
      buildZeroTouchingSegmentRows(user, "childOverallPercent", true).forEach((segment) => {
        const rowNumber = wideRows.length + 1;
        wideRows.push(segment.row);
        extraSeries.push({
          valuesRef: excelSheetRef(
            dataSheetName,
            rowNumber,
            2,
            rowNumber,
            overallStudy2MaxSessions + 1
          ),
          color: segment.color,
          dashed: segment.dashed,
          marker: segment.marker,
        });
      });

      chartSeriesByUser.push({ parentRow, childRow, extraSeries });
      wideRows.push([]);
    });

    const chartFiles = overallStudy2TrendData.map((user, userIndex) => {
      const chartSeries = chartSeriesByUser[userIndex];
      return {
        name: `chart${userIndex + 1}.xml`,
        xml: makeEditableLineChartXml({
          title: user.userName,
          categoriesRef,
          parentNameRef: excelSheetRef(dataSheetName, chartSeries.parentRow, 1),
          parentValuesRef: excelSheetRef(
            dataSheetName,
            chartSeries.parentRow,
            2,
            chartSeries.parentRow,
            overallStudy2MaxSessions + 1
          ),
          childNameRef: excelSheetRef(dataSheetName, chartSeries.childRow, 1),
          childValuesRef: excelSheetRef(
            dataSheetName,
            chartSeries.childRow,
            2,
            chartSeries.childRow,
            overallStudy2MaxSessions + 1
          ),
          extraSeries: chartSeries.extraSeries,
          showXAxis: userIndex === overallStudy2TrendData.length - 1,
          showLegend: userIndex === 0,
        }),
      };
    });

    const longRows = [
      [
        { value: "User", style: 1 },
        { value: "Email", style: 1 },
        { value: "Session", style: 1 },
        { value: "Date", style: 1 },
        { value: "Parent Overall Fidelity %", style: 1 },
        { value: "Child Responsiveness %", style: 1 },
        { value: "Combined %", style: 1 },
      ],
    ];

    overallStudy2TrendData.forEach((user) => {
      user.sessions.forEach((sessionRow, index) => {
        longRows.push([
          user.userName,
          user.userEmail || "—",
          `S${index + 1}`,
          sessionRow.createdAt ? new Date(sessionRow.createdAt).toLocaleString() : "—",
          toExcelPercent(sessionRow.parentOverallPercent),
          toExcelPercent(sessionRow.childOverallPercent),
          toExcelPercent(sessionRow.combinedOverallPercent),
        ]);
      });
    });

    downloadXlsxFile(
      `overall_participant_performance_${statsDataMode === "original" ? "original" : "final"}.xlsx`,
      [
      {
        name: "Graph",
        rows: Array.from({ length: Math.max(1, overallStudy2TrendData.length * 14) }, () => []),
        columnWidths: Array(16).fill(12),
        chartDrawing: {
          xml: makeStackedChartDrawingXml(overallStudy2TrendData.length),
          charts: chartFiles,
        },
      },
      {
        name: dataSheetName,
        rows: wideRows,
        columnWidths: [24, 28, ...Array(overallStudy2MaxSessions).fill(10)],
      },
      {
        name: "Long Data",
        rows: longRows,
        columnWidths: [24, 30, 12, 24, 24, 24, 16],
      },
    ]);
  }, [overallStudy2TrendData, overallStudy2MaxSessions, statsDataMode]);

  const setGraphSessionPhase = useCallback((sessionId, phase) => {
    setGraphPhaseBySession((prev) => {
      const next = { ...prev };
      if (!phase) {
        delete next[sessionId];
      } else {
        next[sessionId] = phase;
      }
      return next;
    });
  }, []);
  const persistGraphSessionPhase = useCallback(
    async (sessionId, phase) => {
      await axios.put(
        `${API}/api/admin/session-phases/${sessionId}`,
        { phase: phase || null },
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
    },
    [session.access_token]
  );
  const toggleGraphPhasePicker = useCallback((phaseId) => {
    setActiveGraphPhase((prev) => (prev === phaseId ? "" : phaseId));
  }, []);
  const setStatsSessionIncluded = useCallback((sessionId, includeInStats) => {
    setStatsInclusionBySession((prev) => ({
      ...prev,
      [sessionId]: includeInStats,
    }));
  }, []);
  const setEditsChildSessionIncluded = useCallback((sessionId, includeInStats) => {
    setEditsChildInclusionBySession((prev) => ({
      ...prev,
      [sessionId]: includeInStats,
    }));
  }, []);
  const persistStatsSessionIncluded = useCallback(
    async (sessionId, includeInStats, includeChildEdits) => {
      await axios.put(
        `${API}/api/admin/session-stats-inclusion/${sessionId}`,
        {
          include_in_stats: includeInStats,
          include_child_edits: includeChildEdits,
        },
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
    },
    [session.access_token]
  );
  const toggleStatsSessionIncluded = useCallback(
    (sessionId, includeInStats) => {
      const includeChildEdits = editsChildInclusionBySession[sessionId] !== false;
      setStatsSessionIncluded(sessionId, includeInStats);
      persistStatsSessionIncluded(sessionId, includeInStats, includeChildEdits)
        .then(() => {
          setStatsInclusionError("");
        })
        .catch((err) => {
          console.error("Failed to save stats session inclusion:", err);
          setStatsInclusionError("Failed to save session inclusion setting.");
          loadStatsInclusionAssignments();
        });
    },
    [
      editsChildInclusionBySession,
      setStatsSessionIncluded,
      persistStatsSessionIncluded,
      loadStatsInclusionAssignments,
    ]
  );
  const toggleEditsChildSessionIncluded = useCallback(
    (sessionId, includeChildEdits) => {
      const includeInStats = statsInclusionBySession[sessionId] !== false;
      setEditsChildSessionIncluded(sessionId, includeChildEdits);
      persistStatsSessionIncluded(sessionId, includeInStats, includeChildEdits)
        .then(() => {
          setStatsInclusionError("");
        })
        .catch((err) => {
          console.error("Failed to save child edit inclusion:", err);
          setStatsInclusionError("Failed to save child edit inclusion setting.");
          loadStatsInclusionAssignments();
        });
    },
    [
      statsInclusionBySession,
      setEditsChildSessionIncluded,
      persistStatsSessionIncluded,
      loadStatsInclusionAssignments,
    ]
  );
  const toggleAllEditsChildSessionsIncluded = useCallback(
    (includeChildEdits) => {
      const rowsToUpdate = editsDisplayRows.filter(
        (row) => row.isIncluded && row.includeChildEdits !== includeChildEdits
      );
      if (!rowsToUpdate.length) return;

      setEditsChildInclusionBySession((prev) => {
        const next = { ...prev };
        rowsToUpdate.forEach((row) => {
          next[row.sessionId] = includeChildEdits;
        });
        return next;
      });

      Promise.all(
        rowsToUpdate.map((row) =>
          persistStatsSessionIncluded(
            row.sessionId,
            statsInclusionBySession[row.sessionId] !== false,
            includeChildEdits
          )
        )
      )
        .then(() => {
          setStatsInclusionError("");
        })
        .catch((err) => {
          console.error("Failed to save bulk child edit inclusion:", err);
          setStatsInclusionError("Failed to save child edit inclusion settings.");
          loadStatsInclusionAssignments();
        });
    },
    [
      editsDisplayRows,
      statsInclusionBySession,
      persistStatsSessionIncluded,
      loadStatsInclusionAssignments,
    ]
  );
  const toggleSessionInActivePhase = useCallback(
    (sessionId) => {
      if (!activeGraphPhase) return;
      const assignedPhase = graphPhaseBySession[sessionId] || "";
      const nextPhase = assignedPhase === activeGraphPhase ? "" : activeGraphPhase;
      setGraphSessionPhase(sessionId, nextPhase);
      persistGraphSessionPhase(sessionId, nextPhase)
        .then(() => {
          setGraphPhaseError("");
        })
        .catch((err) => {
          console.error("Failed to save category assignment:", err);
          setGraphPhaseError("Failed to save category assignment.");
          loadGraphPhaseAssignments();
        });
    },
    [
      activeGraphPhase,
      graphPhaseBySession,
      setGraphSessionPhase,
      persistGraphSessionPhase,
      loadGraphPhaseAssignments,
    ]
  );

  const videoKey = useMemo(() => {
    const firstWithVideo = selectedRows.find(r => !r.isNew && r.object_key);
    return firstWithVideo ? firstWithVideo.object_key : null;
  }, [selectedRows]);
  const resumeTime = useMemo(() => videoTimes[selectedId] || 0, [videoTimes, selectedId]);

  const { tableData, deletedSegments } = useMemo(() => {
    // Preserve the ordering from selectedRows (which already prioritizes new blank rows)
    const sortedActive = selectedRows.slice();

    if (!showHistory) {
      return { tableData: sortedActive, deletedSegments: new Set() };
    }

    // Build deleted segments from latest history when no active row exists
    const latestBySeg = new Map();
    historyRows.forEach((h) => {
      const t = h.historyCreatedAt ? new Date(h.historyCreatedAt).getTime() : 0;
      const prev = latestBySeg.get(h.segment_id);
      if (!prev || t > prev.time) {
        latestBySeg.set(h.segment_id, {
          action: h.historyAction,
          is_deleted: h.is_deleted,
          time: t,
        });
      }
    });
    // Active rows override any history for the same segment
    sortedActive.forEach((r) => {
      latestBySeg.set(r.segment_id, {
        action: "active",
        is_deleted: false,
        time: Number.POSITIVE_INFINITY,
      });
    });
    const deletedSet = new Set();
    latestBySeg.forEach((v, seg) => {
      if (v.action === "delete" && v.is_deleted) deletedSet.add(seg);
    });

    const combined = [...sortedActive, ...historyRows];
    const newRows = combined.filter(r => r.isNew);
    const rest = combined.filter(r => !r.isNew);

    rest.sort((a, b) => {
      if (a.segment_id === b.segment_id) {
        const aHist = !!a.isHistory;
        const bHist = !!b.isHistory;
        if (aHist !== bHist) {
          return aHist ? 1 : -1; // active first
        }
        // Within history for the same segment, newest first (descending)
        const ta = a.historyCreatedAt
          ? new Date(a.historyCreatedAt).getTime()
          : a.created_at
          ? new Date(a.created_at).getTime()
          : 0;
        const tb = b.historyCreatedAt
          ? new Date(b.historyCreatedAt).getTime()
          : b.created_at
          ? new Date(b.created_at).getTime()
          : 0;
        return tb - ta;
      }
      const pa = parseSeconds(a.begin_end);
      const pb = parseSeconds(b.begin_end);
      if (pa !== pb) return pa - pb;
      const ta = a.historyCreatedAt
        ? new Date(a.historyCreatedAt).getTime()
        : a.created_at
        ? new Date(a.created_at).getTime()
        : 0;
      const tb = b.historyCreatedAt
        ? new Date(b.historyCreatedAt).getTime()
        : b.created_at
        ? new Date(b.created_at).getTime()
        : 0;
      return tb - ta;
    });

    // New rows always render at the top; their order follows insertion order.
    return { tableData: [...newRows, ...rest], deletedSegments: deletedSet };
  }, [selectedRows, historyRows, showHistory]);

  // Debugging aid for add-row placement and ordering
  useEffect(() => {
    const newRows = tableData.filter(r => r.isNew);
    if (newRows.length) {
      logDebug("New rows present", {
        newRows: newRows.map(r => ({ seg: r.segment_id, begin_end: r.begin_end, isHistory: r.isHistory })),
        firstRow: tableData[0]?.segment_id,
        firstRowIsNew: !!tableData[0]?.isNew,
      });
    }
  }, [tableData]);

  const mapChildResponse = (val) => {
    if (!val) return null;
    const clean = String(val).trim().toLowerCase();
    const map = {
      "vocal": "Vocal response",
      "vocal response": "Vocal response",
      "gesture": "Gesture response",
      "gesture response": "Gesture response",
      "multi word response": "Multi word response",
      "single word response": "Single word response",
      "multi word initiation": "Multi word initiation",
      "single word initiation": "Single word initiation",
      "vocal initiation": "Vocal initiation",
      "gesture initiation": "Gesture initiation",
      "none": null,
    };
    return map[clean] || val;
  };

  const formatChildTranscript = (response, transcript) => {
    const mapped = mapChildResponse(response);
    if (!mapped) return "-";
    const isGesture = /gesture/i.test(mapped);
    if (isGesture || !transcript || typeof transcript !== "string") return "-";
    const parts = transcript.split("||").map(p => p.trim()).filter(Boolean);
    const kids = parts
      .map(p => {
        const m = p.match(/^(kid|child)\s*[:-]\s*(.*)$/i);
        return m && m[2] ? m[2].trim() : null;
      })
      .filter(Boolean);
    if (kids.length) return kids.join(", ");
    const trimmed = transcript.trim();
    if (isGesture && (!trimmed || trimmed === "-")) return "-";
    return trimmed || "-";
  };

  const statsModalStyle = statsModalFrame
    ? {
        left: `${statsModalFrame.left}px`,
        top: `${statsModalFrame.top}px`,
        width: `${statsModalFrame.width}px`,
        height: `${statsModalFrame.height}px`,
      }
    : undefined;

  const statsModal = showStats && (
    <div className="stats-modal-backdrop" onClick={handleStatsBackdropClick}>
      <div
        ref={statsModalRef}
        className={`stats-modal draggable-stats-modal ${statsBrowseMode ? "stats-modal-browser" : ""} ${
          statsModalFrame ? "is-positioned" : ""
        }`}
        style={statsModalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="stats-modal-header draggable-stats-modal-header" onMouseDown={startStatsModalDrag}>
          <h3>{statsBrowseMode ? "User Study 2 Statistics" : "Session Statistics"}</h3>
          <button
            className="icon-button secondary"
            onClick={closeStatsModal}
          >
            Close
          </button>
        </div>
        {["nw", "ne", "sw", "se"].map((corner) => (
          <span
            key={`stats-resize-${corner}`}
            className={`stats-modal-resize-handle ${corner}`}
            onMouseDown={startStatsModalResize(corner)}
            aria-hidden="true"
          />
        ))}
        <div className="stats-modal-scroll-body">
        <div className={statsBrowseMode ? "stats-browser-layout" : ""}>
          {statsBrowseMode && (
            <aside className="stats-user-sidebar">
              <div className="stats-user-sidebar-header">
                <div>
                  <h4>User Study 2 Users</h4>
                  <p>Select a user to view graphs and edit statistics.</p>
                </div>
                <span className="stats-user-count">{study2StatsUsers.length}</span>
              </div>
              <div className="stats-user-list">
                {study2StatsUsers.length === 0 ? (
                  <p className="muted">No User Study 2 users available.</p>
                ) : (
                  <>
                    <button
                      className={`stats-user-card stats-user-card-overall ${
                        isOverallStatsBrowser ? "active" : ""
                      }`}
                      onClick={() => {
                        setStatsUserId(null);
                        setStatsTab("graph");
                      }}
                    >
                      <span className="stats-user-card-name">Overall Stats</span>
                      <span className="stats-user-card-email">All included User Study 2 participants</span>
                      <span className="stats-user-card-meta">
                        {`${study2StatsUsers.length} user${study2StatsUsers.length === 1 ? "" : "s"}`}
                      </span>
                    </button>
                    {study2StatsUsers.map((user) => {
                      const isActive = user.user_id === selectedUserIdForStats;
                      return (
                        <button
                          key={`stats-user-${user.user_id}`}
                          className={`stats-user-card ${isActive ? "active" : ""}`}
                          onClick={() => {
                            setStatsUserId(user.user_id);
                            setStatsTab("graph");
                          }}
                        >
                          <span className="stats-user-card-name">{user.user_name || "Unnamed user"}</span>
                          <span className="stats-user-card-email">{user.user_email || "—"}</span>
                          <span className="stats-user-card-meta">
                            {`${user.sessions.length} session${user.sessions.length === 1 ? "" : "s"}`}
                          </span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </aside>
          )}

          <div className={statsBrowseMode ? "stats-browser-main" : ""}>
            <div className="stats-meta">
              {statsBrowseMode ? (
                selectedStatsUser ? (
                  <>
                    <span>{selectedStatsUser.user_name || "Unnamed user"}</span>
                    <span>{selectedStatsUser.user_email || "—"}</span>
                    <span>
                      {`${userStudy2SessionsForStats.length} User Study 2 session${
                        userStudy2SessionsForStats.length === 1 ? "" : "s"
                      }`}
                    </span>
                  </>
                ) : (
                  <>
                    <span>Study 2 Users: {study2StatsUsers.length}</span>
                    <span>Select a user from the left to load their graph and edits data.</span>
                  </>
                )
              ) : (
                <>
                  <span>
                    Session Length:{" "}
                    {sessionStats.sessionMinutes
                      ? `${sessionStats.sessionMinutes.toFixed(2)} min`
                      : "—"}
                  </span>
                  <span>Strategy Rows: {sessionStats.totalStrategyRows}</span>
                </>
              )}
            </div>
            <div className="stats-tab-bar">
              {!statsBrowseMode && (
                <button
                  className={`stats-tab-btn ${statsTab === "metrics" ? "active" : ""}`}
                  onClick={() => setStatsTab("metrics")}
                >
                  Metrics
                </button>
              )}
              <button
                className={`stats-tab-btn ${statsTab === "graph" ? "active" : ""}`}
                onClick={() => setStatsTab("graph")}
              >
                Graph
              </button>
              <button
                className={`stats-tab-btn ${statsTab === "edits" ? "active" : ""}`}
                onClick={() => setStatsTab("edits")}
              >
                Edits
              </button>
              <button
                className={`stats-tab-btn ${statsTab === "responses" ? "active" : ""}`}
                onClick={() => setStatsTab("responses")}
              >
                Child Responses
              </button>
            </div>
            {statsBrowseMode && (statsTab === "graph" || statsTab === "responses") && (
              <div className="stats-data-source-row">
                <span>Data source</span>
                <div className="stats-data-source-toggle" role="group" aria-label="Statistics data source">
                  <button
                    className={statsDataMode === "final" ? "active" : ""}
                    onClick={() => setStatsDataMode("final")}
                  >
                    Final
                  </button>
                  <button
                    className={statsDataMode === "original" ? "active" : ""}
                    onClick={() => setStatsDataMode("original")}
                  >
                    Original
                  </button>
                </div>
                <p>
                  {statsDataMode === "original"
                    ? "Showing the model's original rows before admin edits."
                    : "Showing the final admin-reviewed rows."}
                </p>
              </div>
            )}

        {isOverallStatsBrowser ? (
          <div className="stats-empty-state">
            {statsTab === "graph" && originalStatsLoading ? (
              <p className="muted">Loading original model statistics…</p>
            ) : statsTab === "graph" && originalStatsError ? (
              <p className="muted">{originalStatsError}</p>
            ) : statsTab === "graph" && overallStudy2TrendData.length > 0 ? (
              <div className="stats-overall-wrap">
                <div className="stats-overall-header">
                  <div>
                    <h4>Overall Participant Graph</h4>
                    <p>{statsDataMode === "original" ? "Original model rows" : "Final admin-reviewed rows"}</p>
                  </div>
                  <button
                    className="notify-button secondary stats-download-graph-button"
                    onClick={downloadOverallStudy2Excel}
                    disabled={!overallStudy2MaxSessions || originalStatsLoading}
                  >
                    <FiDownload size={15} />
                    Download Excel
                  </button>
                </div>
                <div className="stats-legend">
                  <span className="legend-item">
                    <span className="legend-swatch parent" />
                    Parent Overall Fidelity %
                  </span>
                  <span className="legend-item">
                    <span className="legend-swatch child" />
                    Child Responsiveness %
                  </span>
                </div>
                <svg
                  className="stats-chart stats-overall-chart"
                  viewBox={`0 0 ${overallStudy2Chart.width} ${overallStudy2Chart.height}`}
                  role="img"
                  aria-label="Overall participant performance across sessions"
                >
                  {overallStudy2TrendData.map((userRow, userIndex) => {
                    const labelLines = splitOverallGraphLabel(userRow.userName);
                    const labelX = 18;
                    const labelBaseY =
                      overallStudy2Chart.bandTopAt(userIndex) +
                      overallStudy2Chart.bandPlotHeight / 2 -
                      (labelLines.length - 1) * 7;
                    const parentPath = userRow.sessions.reduce((path, sessionRow, index) => {
                      const value = sessionRow.parentOverallPercent;
                      if (!Number.isFinite(value)) return path;
                      const x = overallStudy2Chart.xAt(index);
                      const y = overallStudy2Chart.yAt(userIndex, value);
                      return `${path}${path ? " L" : "M"} ${x} ${y}`;
                    }, "");
                    const childPath = userRow.sessions.reduce((path, sessionRow, index) => {
                      const value = sessionRow.childOverallPercent;
                      if (!Number.isFinite(value)) return path;
                      const x = overallStudy2Chart.xAt(index);
                      const y = overallStudy2Chart.yAt(userIndex, value);
                      return `${path}${path ? " L" : "M"} ${x} ${y}`;
                    }, "");

                    return (
                      <g key={`overall-user-${userRow.userId}`}>
                        <rect
                          x={overallStudy2Chart.padLeft}
                          y={overallStudy2Chart.bandTopAt(userIndex)}
                          width={overallStudy2Chart.plotW}
                          height={overallStudy2Chart.bandPlotHeight}
                          fill="#ffffff"
                          stroke="#cbd5e1"
                          strokeWidth="1"
                        />
                        {overallStudy2Chart.yTicks.map((tick) => (
                          <g key={`overall-${userRow.userId}-${tick}`}>
                            <text
                              x={overallStudy2Chart.padLeft - 8}
                              y={overallStudy2Chart.yAt(userIndex, tick) + 4}
                              textAnchor="end"
                              fontSize="10.5"
                              fill="#6b7280"
                            >
                              {tick}
                            </text>
                          </g>
                        ))}
                        <line
                          x1={overallStudy2Chart.padLeft}
                          x2={overallStudy2Chart.padLeft}
                          y1={overallStudy2Chart.bandTopAt(userIndex)}
                          y2={overallStudy2Chart.bandBottomAt(userIndex)}
                          stroke="#9ca3af"
                          strokeWidth="1"
                        />
                        <line
                          x1={overallStudy2Chart.padLeft}
                          x2={overallStudy2Chart.width - overallStudy2Chart.padRight}
                          y1={overallStudy2Chart.bandBottomAt(userIndex)}
                          y2={overallStudy2Chart.bandBottomAt(userIndex)}
                          stroke="#9ca3af"
                          strokeWidth="1"
                        />
                        <text
                          x={labelX}
                          y={labelBaseY}
                          textAnchor="start"
                          fontSize="11.5"
                          fontWeight="700"
                          fill="#111827"
                        >
                          {labelLines.map((line, idx) => (
                            <tspan key={`${userRow.userId}-label-${idx}`} x={labelX} dy={idx === 0 ? 0 : 14}>
                              {line}
                            </tspan>
                          ))}
                        </text>
                        {parentPath && (
                          <path
                            d={parentPath}
                            fill="none"
                            stroke="#2563eb"
                            strokeWidth="2.4"
                          />
                        )}
                        {childPath && (
                          <path
                            d={childPath}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2.4"
                            strokeDasharray="7 5"
                          />
                        )}
                        {userRow.sessions.map((sessionRow, index) => {
                          const x = overallStudy2Chart.xAt(index);
                          return (
                            <g key={`overall-point-${userRow.userId}-${sessionRow.sessionId}`}>
                              {Number.isFinite(sessionRow.parentOverallPercent) && (
                                <circle
                                  cx={x}
                                  cy={overallStudy2Chart.yAt(userIndex, sessionRow.parentOverallPercent)}
                                  r="3.8"
                                  fill="#2563eb"
                                />
                              )}
                              {Number.isFinite(sessionRow.childOverallPercent) && (
                                <circle
                                  cx={x}
                                  cy={overallStudy2Chart.yAt(userIndex, sessionRow.childOverallPercent)}
                                  r="3.8"
                                  fill="#10b981"
                                />
                              )}
                            </g>
                          );
                        })}
                      </g>
                    );
                  })}
                  {Array.from({ length: overallStudy2MaxSessions }, (_, index) => (
                    <text
                      key={`overall-x-${index}`}
                      x={overallStudy2Chart.xAt(index)}
                      y={overallStudy2Chart.height - 10}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#6b7280"
                    >
                      {`S${index + 1}`}
                    </text>
                  ))}
                </svg>
                <p className="stats-overall-footnote">
                  Select a user from the left to open their individual graph and edits view.
                </p>
              </div>
            ) : statsTab === "responses" ? (
              <div className="stats-empty-state">
                <p>Select a user from the left to view their child response counts by category.</p>
              </div>
            ) : statsTab === "edits" ? (
              renderEditsStatsTable({ showUserColumn: true })
            ) : (
              <>
                <h4>Select a User Study 2 user</h4>
                <p>The graph and edits tables will load here after you choose a user from the list.</p>
              </>
            )}
          </div>
        ) : statsTab === "metrics" ? (
          <table className="stats-table">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              <tr className="stats-section-row fidelity">
                <td colSpan={3}>Fidelity Scores</td>
              </tr>
              <tr>
                <td>Overall</td>
                <td>{formatPercent(sessionStats.fidelityAll.percent)}</td>
                <td>{`${sessionStats.fidelityAll.sumFours}/${sessionStats.fidelityAll.sumScores}`}</td>
              </tr>
              <tr>
                <td>Time Delay</td>
                <td>{formatPercent(sessionStats.fidelityTimeDelay.percent)}</td>
                <td>{`${sessionStats.fidelityTimeDelay.sumFours}/${sessionStats.fidelityTimeDelay.sumScores}`}</td>
              </tr>
              <tr>
                <td>Modeling + Mand-Model</td>
                <td>{formatPercent(sessionStats.fidelityModelingMand.percent)}</td>
                <td>{`${sessionStats.fidelityModelingMand.sumFours}/${sessionStats.fidelityModelingMand.sumScores}`}</td>
              </tr>
              <tr className="stats-section-row strategy">
                <td colSpan={3}>Strategy Rates</td>
              </tr>
              <tr>
                <td>Overall</td>
                <td>{formatRate(sessionStats.rateAll.rate)}</td>
                <td>{`${sessionStats.rateAll.count} / ${
                  sessionStats.sessionMinutes
                    ? sessionStats.sessionMinutes.toFixed(2)
                    : "—"
                } min`}</td>
              </tr>
              <tr>
                <td>Time Delay</td>
                <td>{formatRate(sessionStats.rateTimeDelay.rate)}</td>
                <td>{`${sessionStats.rateTimeDelay.count} / ${
                  sessionStats.sessionMinutes
                    ? sessionStats.sessionMinutes.toFixed(2)
                    : "—"
                } min`}</td>
              </tr>
              <tr>
                <td>Modeling + Mand-Model</td>
                <td>{formatRate(sessionStats.rateModelingMand.rate)}</td>
                <td>{`${sessionStats.rateModelingMand.count} / ${
                  sessionStats.sessionMinutes
                    ? sessionStats.sessionMinutes.toFixed(2)
                    : "—"
                } min`}</td>
              </tr>
              <tr className="stats-section-row child">
                <td colSpan={3}>Child Response Rates</td>
              </tr>
              <tr>
                <td>Overall</td>
                <td>{formatPercent(sessionStats.childRateAll.percent)}</td>
                <td>{`${sessionStats.childRateAll.responses}/${sessionStats.childRateAll.opportunities}`}</td>
              </tr>
              <tr>
                <td>Time Delay</td>
                <td>{formatPercent(sessionStats.childRateTimeDelay.percent)}</td>
                <td>{`${sessionStats.childRateTimeDelay.responses}/${sessionStats.childRateTimeDelay.opportunities}`}</td>
              </tr>
              <tr>
                <td>Modeling + Mand-Model</td>
                <td>{formatPercent(sessionStats.childRateModelingMand.percent)}</td>
                <td>{`${sessionStats.childRateModelingMand.responses}/${sessionStats.childRateModelingMand.opportunities}`}</td>
              </tr>
            </tbody>
          </table>
        ) : statsTab === "graph" ? (
          <div className="stats-graph-wrap">
            <div className="stats-inclusion-note">
              Uncheck Use to exclude a session from the graph, numbering, and graph/edit averages.
            </div>
            {statsInclusionLoading && <p className="muted">Loading session inclusion settings…</p>}
            {statsInclusionError && <p className="muted">{statsInclusionError}</p>}
            <div className="stats-legend-row">
              <div className="stats-legend">
                <span className="legend-item">
                  <span className="legend-swatch parent" />
                  Parent Overall Fidelity %
                </span>
                <span className="legend-item">
                  <span className="legend-swatch child" />
                  Child Responsiveness %
                </span>
              </div>
              <button
                className="notify-button secondary stats-download-graph-button"
                onClick={downloadStatsGraph}
                disabled={!statsTrendData.length || originalStatsLoading}
              >
                <FiDownload size={15} />
                Download Graph
              </button>
            </div>
            {statsTrendData.length === 0 ? (
              <p className="muted">
                {userStudy2SessionsForStats.length
                  ? "No included sessions are currently selected for this user."
                  : "No session data available for this user."}
              </p>
            ) : (
              <>
                <svg
                  className="stats-chart"
                  viewBox={`0 0 ${trendChart.width} ${trendChart.height}`}
                  role="img"
                  aria-label="Parent and child performance across sessions"
                >
                  {statsTrendData.map((row, i) => {
                    const phaseId = graphPhaseBySession[row.sessionId];
                    const phase = GRAPH_PHASES.find((item) => item.id === phaseId);
                    if (!phase) return null;
                    const bandWidth =
                      statsTrendData.length > 1
                        ? trendChart.plotW / (statsTrendData.length - 1)
                        : trendChart.plotW;
                    const leftRaw =
                      statsTrendData.length > 1
                        ? trendChart.xAt(i) - bandWidth / 2
                        : trendChart.padLeft;
                    const rightRaw =
                      statsTrendData.length > 1
                        ? trendChart.xAt(i) + bandWidth / 2
                        : trendChart.padLeft + trendChart.plotW;
                    const left = Math.max(trendChart.padLeft, leftRaw);
                    const right = Math.min(trendChart.padLeft + trendChart.plotW, rightRaw);
                    const width = Math.max(0, right - left);
                    if (width <= 0) return null;
                    return (
                      <rect
                        key={`phase-bg-${row.sessionId}`}
                        x={left}
                        y={trendChart.padTop}
                        width={width}
                        height={trendChart.plotH}
                        fill={phase.chartFill}
                      />
                    );
                  })}
                  {trendChart.yTicks.map((tick) => (
                    <g key={tick}>
                      <line
                        x1={trendChart.padLeft}
                        x2={trendChart.width - 20}
                        y1={trendChart.yAt(tick)}
                        y2={trendChart.yAt(tick)}
                        stroke="#e5e7eb"
                        strokeWidth="1"
                      />
                      <text
                        x={trendChart.padLeft - 8}
                        y={trendChart.yAt(tick) + 4}
                        textAnchor="end"
                        fontSize="11"
                        fill="#6b7280"
                      >
                        {tick}
                      </text>
                    </g>
                  ))}

                  <line
                    x1={trendChart.padLeft}
                    x2={trendChart.padLeft}
                    y1={16}
                    y2={trendChart.height - trendChart.padBottom}
                    stroke="#9ca3af"
                    strokeWidth="1.2"
                  />
                  <line
                    x1={trendChart.padLeft}
                    x2={trendChart.width - 20}
                    y1={trendChart.height - trendChart.padBottom}
                    y2={trendChart.height - trendChart.padBottom}
                    stroke="#9ca3af"
                    strokeWidth="1.2"
                  />
                  {activeTrendIndex >= 0 && (
                    <line
                      x1={trendChart.xAt(activeTrendIndex)}
                      x2={trendChart.xAt(activeTrendIndex)}
                      y1={16}
                      y2={trendChart.height - trendChart.padBottom}
                      stroke="#f59e0b"
                      strokeDasharray="4 4"
                      strokeWidth="1.5"
                    />
                  )}

                  {trendChart.parentPath && (
                    <path
                      d={trendChart.parentPath}
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="2.5"
                    />
                  )}
                  {trendChart.childPath && (
                    <path
                      d={trendChart.childPath}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2.5"
                    />
                  )}

                  {statsTrendData.map((row, i) => {
                    const x = trendChart.xAt(i);
                    const parentValue = row.parentOverallPercent;
                    const childValue = row.childOverallPercent;
                    const isActiveSession = row.sessionId === activeStatsSessionId;
                    return (
                      <g key={row.sessionId}>
                        {Number.isFinite(parentValue) && (
                          <circle
                            cx={x}
                            cy={trendChart.yAt(parentValue)}
                            r={isActiveSession ? "6" : "4"}
                            fill="#2563eb"
                            stroke={isActiveSession ? "#1e3a8a" : "none"}
                            strokeWidth={isActiveSession ? "2" : "0"}
                          />
                        )}
                        {Number.isFinite(childValue) && (
                          <circle
                            cx={x}
                            cy={trendChart.yAt(childValue)}
                            r={isActiveSession ? "6" : "4"}
                            fill="#10b981"
                            stroke={isActiveSession ? "#065f46" : "none"}
                            strokeWidth={isActiveSession ? "2" : "0"}
                          />
                        )}
                        <text
                          x={x}
                          y={trendChart.height - trendChart.padBottom + 18}
                          textAnchor="middle"
                          fontSize="10"
                          fill={isActiveSession ? "#b45309" : "#6b7280"}
                          fontWeight={isActiveSession ? "700" : "400"}
                        >
                          {`S${row.index}`}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                <div className="stats-phase-controls">
                  {GRAPH_PHASES.map((phase) => (
                    <button
                      key={`phase-picker-${phase.id}`}
                      className={`stats-phase-btn phase-${phase.id} ${
                        activeGraphPhase === phase.id ? "active" : ""
                      }`}
                      onClick={() => toggleGraphPhasePicker(phase.id)}
                    >
                      {phase.label}
                    </button>
                  ))}
                </div>
                {activeGraphPhase && (
                  <div className="stats-phase-message">
                    <span>
                      Select sessions to assign to{" "}
                      {GRAPH_PHASES.find((phase) => phase.id === activeGraphPhase)?.label}.
                    </span>
                    <button
                      className="icon-button secondary"
                      onClick={() => setActiveGraphPhase("")}
                    >
                      Done
                    </button>
                  </div>
                )}
                {graphPhaseLoading && <p className="muted">Loading category assignments…</p>}
                {graphPhaseError && <p className="muted">{graphPhaseError}</p>}

                <table className="stats-table stats-session-table">
                  <thead>
                    <tr>
                      <th>Use</th>
                      {activeGraphPhase && <th>Select</th>}
                      <th>Session</th>
                      <th>Date</th>
                      <th>Rows</th>
                      <th>Parent Overall %</th>
                      <th>Child Response %</th>
                      <th>Combined %</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsGraphTableRows.map((row) => {
                      const phaseId = graphPhaseBySession[row.sessionId] || "";
                      const phase = GRAPH_PHASES.find((item) => item.id === phaseId);
                      const isActiveSession = row.sessionId === activeStatsSessionId;
                      const rowClassName = [
                        isActiveSession ? "stats-row-current" : "",
                        !row.isIncluded ? "stats-row-excluded" : "",
                        phaseId ? `stats-phase-row phase-${phaseId}` : "",
                        activeGraphPhase ? "stats-row-selectable" : "",
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <tr
                          key={`stats-${row.sessionId}`}
                          className={rowClassName}
                          onClick={() => {
                            if (!activeGraphPhase) return;
                            toggleSessionInActivePhase(row.sessionId);
                          }}
                        >
                          <td className="stats-session-toggle-cell">
                            <input
                              type="checkbox"
                              className="stats-session-checkbox"
                              checked={row.isIncluded}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleStatsSessionIncluded(row.sessionId, e.target.checked);
                              }}
                            />
                          </td>
                          {activeGraphPhase && (
                            <td>
                              <button
                                className={`stats-select-tick ${
                                  phaseId === activeGraphPhase ? "selected" : ""
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleSessionInActivePhase(row.sessionId);
                                }}
                              >
                                {phaseId === activeGraphPhase ? "✓" : "○"}
                              </button>
                            </td>
                          )}
                          <td>{row.index ? `S${row.index}` : <span className="stats-session-excluded">Excluded</span>}</td>
                          <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                          <td>{row.validRows}</td>
                          <td>{formatPercent(row.parentOverallPercent)}</td>
                          <td>{formatPercent(row.childOverallPercent)}</td>
                          <td>{formatPercent(row.combinedOverallPercent)}</td>
                          <td>
                            {phase ? (
                              <span className={`stats-phase-pill phase-${phase.id}`}>{phase.label}</span>
                            ) : (
                              "Unassigned"
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="stats-summary-row">
                      <td>—</td>
                      {activeGraphPhase && <td>—</td>}
                      <td>Average</td>
                      <td>{`Across ${graphAverages.overall.sessionsCount} sessions`}</td>
                      <td>—</td>
                      <td>{formatPercent(graphAverages.overall.parentAvg)}</td>
                      <td>{formatPercent(graphAverages.overall.childAvg)}</td>
                      <td>{formatPercent(graphAverages.overall.combinedAvg)}</td>
                      <td>—</td>
                    </tr>
                  </tfoot>
                </table>

                <table className="stats-table stats-phase-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Sessions</th>
                      <th>Avg Parent %</th>
                      <th>Avg Child %</th>
                      <th>Avg Combined %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {GRAPH_PHASES.map((phase) => {
                      const metric = graphAverages.byPhase[phase.id];
                      return (
                        <tr key={`phase-summary-${phase.id}`}>
                          <td>{phase.label}</td>
                          <td>{metric.sessionsCount}</td>
                          <td>{formatPercent(metric.parentAvg)}</td>
                          <td>{formatPercent(metric.childAvg)}</td>
                          <td>{formatPercent(metric.combinedAvg)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
	        ) : statsTab === "responses" ? (
	          renderChildResponsePhaseTable()
	        ) : (
	          renderEditsStatsTable()
	        )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) =>
      `${row.segment_id || "seg"}-${row.historyId || row.historyCreatedAt || row.created_at || "base"}-${
        row.isHistory ? "h" : "a"
      }`
  });

  if (loading) return <p style={{ padding: 32 }}>Loading…</p>;

  const baselineUploadModal = showBaselineUpload ? (
    <div className="stats-modal-backdrop" onClick={closeBaselineUploadModal}>
      <div className="stats-modal baseline-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="stats-modal-header">
          <h3>Upload Baseline Video</h3>
          <button className="notify-button secondary" onClick={closeBaselineUploadModal}>
            Close
          </button>
        </div>
        <p className="baseline-upload-note">
          This admin flow creates a session with your chosen date and uploads the video without sending any emails or push notifications.
        </p>
        <form
          className="baseline-upload-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setAdminUploadError("");

            if (!baselineUploadForm.userId) {
              setAdminUploadError("Please choose a user.");
              return;
            }
            if (!baselineUploadForm.createdAt) {
              setAdminUploadError("Please choose a session date.");
              return;
            }
            if (!baselineUploadForm.file) {
              setAdminUploadError("Please choose a video file.");
              return;
            }

            setAdminUploadSubmitting(true);
            try {
              const createdAtIso = new Date(baselineUploadForm.createdAt).toISOString();
              const { data } = await axios.post(
                `${API}/api/admin/baseline-upload/init`,
                {
                  user_id: baselineUploadForm.userId,
                  created_at: createdAtIso,
                },
                {
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                  },
                }
              );

              await new Promise((resolve, reject) => {
                const upload = new tus.Upload(baselineUploadForm.file, {
                  endpoint: `${API}/upload`,
                  retryDelays: [0, 3000, 5000, 10000],
                  storeFingerprintForResuming: false,
                  chunkSize: 8 * 1024 * 1024,
                  metadata: {
                    filename: baselineUploadForm.file.name,
                    filetype: baselineUploadForm.file.type || "video/mp4",
                    videoId: data.sessionId,
                    userId: baselineUploadForm.userId,
                    adminUploadTicket: data.uploadTicket,
                  },
                  onProgress: (bytesSent, bytesTotal) => {
                    if (!bytesTotal) return;
                    setAdminUploadProgress(Math.round((bytesSent / bytesTotal) * 100));
                  },
                  onSuccess: () => resolve(),
                  onError: (err) => reject(err),
                });
                upload.start();
              });

              await fetchRows();
              setViewMode("names");
              setSelectedUserId(baselineUploadForm.userId);
              closeBaselineUploadModal();
              alert(
                `Baseline video uploaded for ${data?.user?.name || data?.user?.email || "user"}.\nSession date: ${new Date(
                  data?.created_at || createdAtIso
                ).toLocaleString()}`
              );
            } catch (err) {
              console.error("Admin baseline upload failed:", err);
              setAdminUploadError(
                err?.response?.data?.error || "Failed to upload baseline video."
              );
            } finally {
              setAdminUploadSubmitting(false);
            }
          }}
        >
          <label className="baseline-upload-field">
            <span>User</span>
            <select
              value={baselineUploadForm.userId}
              onChange={(e) =>
                setBaselineUploadForm((prev) => ({ ...prev, userId: e.target.value }))
              }
              disabled={adminUploadSubmitting || adminUsersLoading}
            >
              <option value="">Select a user</option>
              {adminUploadUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {(user.name || "Unnamed user") + (user.email ? ` (${user.email})` : "")}
                </option>
              ))}
            </select>
          </label>

          <label className="baseline-upload-field">
            <span>Session date</span>
            <input
              type="datetime-local"
              value={baselineUploadForm.createdAt}
              onChange={(e) =>
                setBaselineUploadForm((prev) => ({ ...prev, createdAt: e.target.value }))
              }
              disabled={adminUploadSubmitting}
            />
          </label>

          <label className="baseline-upload-field">
            <span>Video file</span>
            <input
              ref={baselineVideoInputRef}
              type="file"
              accept="video/*"
              onChange={(e) =>
                setBaselineUploadForm((prev) => ({
                  ...prev,
                  file: e.target.files?.[0] || null,
                }))
              }
              disabled={adminUploadSubmitting}
            />
          </label>

          {adminUploadError ? <div className="baseline-upload-error">{adminUploadError}</div> : null}

          {adminUploadSubmitting ? (
            <div className="baseline-upload-progress">
              Uploading baseline video... {adminUploadProgress}%
            </div>
          ) : null}

          <div className="baseline-upload-actions">
            <button
              type="button"
              className="notify-button secondary"
              onClick={closeBaselineUploadModal}
              disabled={adminUploadSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="notify-button" disabled={adminUploadSubmitting}>
              {adminUploadSubmitting ? `Uploading... ${adminUploadProgress}%` : "Create Session + Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;

  return (
    <div className="admin-wrapper">
      {statsModal}
      {baselineUploadModal}

      {!selectedId ? (
        <>
          <h2>
            Admin –{" "}
            {adminTab === "analysis"
              ? viewMode === "sessions"
                ? "Sessions"
                : selectedUserId
                ? "User Sessions"
                : "Users"
              : "Reliability (Videos Only)"}
          </h2>
          <div className="admin-actions-bar main-admin-toolbar">
            <div className="admin-actions-left">
              <div className="admin-actions-row">
                <div className="view-toggle grouped-toggle">
                  {!isReliabilityOnly && (
                    <button
                      className={`notify-button ${adminTab === "analysis" ? "" : "secondary"}`}
                      onClick={() => setAdminTab("analysis")}
                    >
                      Analysis
                    </button>
                  )}
                  <button
                    className={`notify-button ${adminTab === "reliability" ? "" : "secondary"}`}
                    onClick={() => setAdminTab("reliability")}
                  >
                    Reliability (Videos Only)
                  </button>
                </div>
                <div className="view-toggle grouped-toggle">
                  <button
                    className={`notify-button ${viewMode === "sessions" ? "" : "secondary"}`}
                    onClick={() => {
                      setViewMode("sessions");
                      setSelectedUserId(null);
                    }}
                  >
                    View by Sessions
                  </button>
                  <button
                    className={`notify-button ${viewMode === "names" ? "" : "secondary"}`}
                    onClick={() => {
                      setViewMode("names");
                      setSelectedUserId(null);
                    }}
                  >
                    View by Name
                  </button>
                </div>
              </div>
              {adminTab === "analysis" && (
                <div className="admin-actions-row">
                  <div className="view-toggle grouped-toggle study-filter-toggle">
                    <button
                      className={`notify-button ${studyFilter === "all" ? "" : "secondary"}`}
                      onClick={() => setStudyFilter("all")}
                    >
                      All
                    </button>
                    <button
                      className={`notify-button ${studyFilter === "study1" ? "" : "secondary"}`}
                      onClick={() => setStudyFilter("study1")}
                    >
                      User Study 1
                    </button>
                    <button
                      className={`notify-button ${studyFilter === "study2" ? "" : "secondary"}`}
                      onClick={() => setStudyFilter("study2")}
                    >
                      User Study 2
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="admin-actions-right">
              {adminTab === "analysis" && (
                <button
                  className="notify-button stats-launch-button"
                  onClick={openStatsBrowser}
                >
                  View All Stats
                </button>
              )}
              {adminTab === "analysis" && (
                <button
                  className="notify-button baseline-upload-button"
                  onClick={() => openBaselineUploadModal(selectedUserId || "")}
                >
                  Upload Baseline Video
                </button>
              )}
              <button className="notify-button secondary" onClick={handleSwitchToMain}>
                Switch to Main
              </button>
              <button className="notify-button" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          </div>

          {adminTab === "analysis" && (
            <div className="study-summary">
              <span>Total: {studyCounts.total}</span>
              <span>User Study 1: {studyCounts.study1}</span>
              <span>User Study 2: {studyCounts.study2}</span>
            </div>
          )}

          {adminTab === "analysis" && viewMode === "sessions" && (
            <>
	              <table className="session-table">
	                <thead>
	                  <tr>
	                  <th>Date</th><th>Name</th><th>Email</th><th>Notifications Sent</th><th>Last Notification</th><th>Study</th><th>Category</th><th>Stats</th>
	                </tr>
	              </thead>
	              <tbody>
	                {pagedSessions.map(s => (
                  <tr
                    key={s.session_id}
                    className="session-row-clickable"
                    onClick={() => setSelectedId(s.session_id)}
                    title="Open session"
                  >
                      <td>{new Date(s.created_at).toLocaleString()}</td>
                      <td>
                        <button
                          className="link-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewMode("names");
                            setSelectedUserId(s.user_id);
                          }}
                        >
                          {s.user_name}
                        </button>
                      </td>
	                      <td>{s.user_email}</td>
	                      <td>{s.email_sent_count ?? 0}</td>
	                      <td>{s.last_email_sent_at ? new Date(s.last_email_sent_at).toLocaleString() : "—"}</td>
	                      <td><span className="study-badge">{getStudyLabel(s.created_at)}</span></td>
	                      <td>
	                        {getSessionPhaseMeta(s.session_phase) ? (
	                          <span className={`session-phase-badge phase-${getSessionPhaseMeta(s.session_phase).id}`}>
	                            {getSessionPhaseMeta(s.session_phase).label}
	                          </span>
	                        ) : "—"}
	                      </td>
	                      <td>
	                        <button
	                          className="notify-button row-stats-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatsForSession(s.session_id);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pager">
                <button onClick={() => setPg(p => Math.max(0, p-1))}
                        disabled={page === 0}>◀ Prev</button>
                <span>{page+1} / {Math.ceil(sessions.length / pageSize)}</span>
                <button onClick={() => setPg(p => p+1)}
                        disabled={(page+1)*pageSize >= sessions.length}>Next ▶</button>
              </div>
            </>
          )}

          {adminTab === "analysis" && viewMode === "names" && !selectedUserId && (
            <>
              <div className="session-command-bar">
                <div className="session-command-copy">
                  <strong>Overall Graph</strong>
                  <span>Open the combined User Study 2 graph and user stats browser.</span>
                </div>
                <button
                  className="notify-button stats-launch-button"
                  onClick={openStatsBrowser}
                >
                  View Overall Graph
                </button>
              </div>
              <table className="session-table">
                <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Sessions</th><th>Last Session</th><th>Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.map(u => (
                      <tr key={u.user_id}>
                      <td>{u.user_name}</td>
                      <td>{u.user_email}</td>
                      <td>{u.sessions.length}</td>
                      <td>{u.latest ? new Date(u.latest).toLocaleString() : "—"}</td>
                      <td>
                        <button className="notify-button" onClick={() => setSelectedUserId(u.user_id)}>
                          View Sessions
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {adminTab === "analysis" && viewMode === "names" && selectedUserId && (
            <>
              <button className="back-button" onClick={() => setSelectedUserId(null)}>
                ← Back to Users
              </button>
	              <table className="session-table">
	                <thead>
	                  <tr>
	                    <th>Date</th><th>Name</th><th>Email</th><th>Notifications Sent</th><th>Last Notification</th><th>Study</th><th>Category</th><th>Stats</th>
	                  </tr>
	                </thead>
                <tbody>
                  {sessions
                    .filter(s => s.user_id === selectedUserId)
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map(s => (
                      <tr
                        key={s.session_id}
                        className="session-row-clickable"
                        onClick={() => setSelectedId(s.session_id)}
                        title="Open session"
                      >
                        <td>{new Date(s.created_at).toLocaleString()}</td>
                        <td>{s.user_name}</td>
	                        <td>{s.user_email}</td>
	                        <td>{s.email_sent_count ?? 0}</td>
	                        <td>{s.last_email_sent_at ? new Date(s.last_email_sent_at).toLocaleString() : "—"}</td>
	                        <td><span className="study-badge">{getStudyLabel(s.created_at)}</span></td>
	                        <td>
	                          {getSessionPhaseMeta(s.session_phase) ? (
	                            <span className={`session-phase-badge phase-${getSessionPhaseMeta(s.session_phase).id}`}>
	                              {getSessionPhaseMeta(s.session_phase).label}
	                            </span>
	                          ) : "—"}
	                        </td>
	                        <td>
	                          <button
	                            className="notify-button row-stats-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openStatsForSession(s.session_id);
                            }}
                          >
                            View
                          </button>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {adminTab === "reliability" && viewMode === "names" && !selectedUserId && (
            <table className="session-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Sessions</th><th>Latest</th><th>Open</th>
                </tr>
              </thead>
              <tbody>
                {reliabilityUsersList.map(u => (
                  <tr key={u.user_id}>
                    <td>{u.user_name}</td>
                    <td>{u.user_email}</td>
                    <td>{u.sessions.length}</td>
                    <td>{u.latest ? new Date(u.latest).toLocaleString() : "—"}</td>
                    <td>
                      <button className="notify-button" onClick={() => setSelectedUserId(u.user_id)}>
                        View Sessions
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {adminTab === "reliability" && viewMode === "names" && selectedUserId && (
            <>
              <button className="back-button" onClick={() => setSelectedUserId(null)}>
                ← Back to Users
              </button>
	              <table className="session-table">
	                <thead>
	                  <tr>
	                    <th>Date</th><th>Name</th><th>Email</th><th>Study</th><th>Category</th><th>Stats</th>
	                  </tr>
	                </thead>
                <tbody>
                  {reliabilitySessions
                    .filter(s => s.user_id === selectedUserId)
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map(s => (
                      <tr
                        key={s.session_id}
                        className="session-row-clickable"
                        onClick={() => setSelectedId(s.session_id)}
                        title="Open session"
                      >
                        <td>{new Date(s.created_at).toLocaleString()}</td>
	                        <td>{s.user_name}</td>
	                        <td>{s.user_email}</td>
	                        <td><span className="study-badge">{getStudyLabel(s.created_at)}</span></td>
	                        <td>
	                          {getSessionPhaseMeta(s.session_phase) ? (
	                            <span className={`session-phase-badge phase-${getSessionPhaseMeta(s.session_phase).id}`}>
	                              {getSessionPhaseMeta(s.session_phase).label}
	                            </span>
	                          ) : "—"}
	                        </td>
	                        <td>
	                          <button
	                            className="notify-button row-stats-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openStatsForSession(s.session_id);
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          {adminTab === "reliability" && viewMode === "sessions" && (
	            <table className="session-table">
	              <thead>
	                <tr>
	                  <th>Date</th><th>Name</th><th>Email</th><th>Study</th><th>Category</th><th>Stats</th>
	                </tr>
	              </thead>
              <tbody>
                {reliabilitySessions
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(s => (
                    <tr
                      key={s.session_id}
                      className="session-row-clickable"
                      onClick={() => setSelectedId(s.session_id)}
                      title="Open session"
                    >
                      <td>{new Date(s.created_at).toLocaleString()}</td>
	                      <td>{s.user_name}</td>
	                      <td>{s.user_email}</td>
	                      <td><span className="study-badge">{getStudyLabel(s.created_at)}</span></td>
	                      <td>
	                        {getSessionPhaseMeta(s.session_phase) ? (
	                          <span className={`session-phase-badge phase-${getSessionPhaseMeta(s.session_phase).id}`}>
	                            {getSessionPhaseMeta(s.session_phase).label}
	                          </span>
	                        ) : "—"}
	                      </td>
	                      <td>
	                        <button
	                          className="notify-button row-stats-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openStatsForSession(s.session_id);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
    adminTab === "analysis" ? (
    <>
          <button className="back-button"   onClick={() => {
              setSelectedId(null);
              navigate("/admin", { replace: true });
            }}>
            ← Back
          </button>
	          <h2>
	            {new Date(selectedRows[0]?.created_at).toLocaleString()} — {selectedRows[0]?.user_name}
	            <span className="study-badge" style={{ marginLeft: "0.75rem" }}>{getStudyLabel(selectedRows[0]?.created_at)}</span>
	            {getSessionPhaseMeta(selectedRows[0]?.session_phase) ? (
	              <span
	                className={`session-phase-badge phase-${getSessionPhaseMeta(selectedRows[0]?.session_phase).id}`}
	                style={{ marginLeft: "0.75rem" }}
	              >
	                {getSessionPhaseMeta(selectedRows[0]?.session_phase).label}
	              </span>
	            ) : null}
	          </h2>
          <div className="admin-actions-bar">
            <div className="view-toggle">
              {!isReliabilityOnly && (
                <button
                  className={`notify-button ${adminTab === "analysis" ? "" : "secondary"}`}
                  onClick={() => setAdminTab("analysis")}
                >
                  Analysis
                </button>
              )}
              <button
                className={`notify-button ${adminTab === "reliability" ? "" : "secondary"}`}
                onClick={() => setAdminTab("reliability")}
              >
                Reliability (Videos Only)
              </button>
            </div>
            <div className="admin-actions-right">
              <button className="notify-button secondary" onClick={handleSwitchToMain}>
                Switch to Main
              </button>
              <button className="notify-button" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          </div>

          <div className="session-command-bar">
            <div className="session-command-actions">
              <button
                onClick={() => openBaselineUploadModal(selectedRows[0]?.user_id || "")}
                className="notify-button baseline-upload-button"
              >
                Upload Baseline Video
              </button>
              <button
                onClick={async () => {
                  const userEmail = selectedRows[0]?.user_email;
                  const videoId = selectedId;
                  const time = new Date(selectedRows[0]?.created_at).toLocaleString();

                  if (!videoId || !userEmail) {
                    alert("User email or session ID missing");
                    return;
                  }

                  try {
                    await axios.post(
                      `${API}/api/admin/notify-user`,
                      { userEmail, videoId, time, channel: "email" },
                      { headers: { Authorization: `Bearer ${session.access_token}` } }
                    );
                    await fetchRows();
                    alert("Email sent to the user successfully.");
                  } catch (err) {
                    console.error(err);
                    alert(err?.response?.data?.error || "Failed to send email.");
                  }
                }}
                className="notify-button"
              >
                Send Email
              </button>
              <button
                onClick={async () => {
                  const userEmail = selectedRows[0]?.user_email;
                  const videoId = selectedId;
                  const time = new Date(selectedRows[0]?.created_at).toLocaleString();

                  if (!videoId) {
                    alert("Session ID missing");
                    return;
                  }

                  try {
                    await axios.post(
                      `${API}/api/admin/notify-user`,
                      { userEmail, videoId, time, channel: "push" },
                      { headers: { Authorization: `Bearer ${session.access_token}` } }
                    );
                    await fetchRows();
                    alert("Push notification sent to the user.");
                  } catch (err) {
                    console.error(err);
                    alert(err?.response?.data?.error || "Failed to send push notification.");
                  }
                }}
                className="notify-button secondary"
              >
                Send Push
              </button>
              {selectedId && (
                <button
                  onClick={() => downloadCSV(selectedRows)}
                  className="notify-button secondary csv-save-button"
                  title="Download analysis as CSV"
                >
                  <FiDownload size={16} />
                  <span>Save as CSV</span>
                </button>
              )}
              <button
                onClick={() => {
                  setStatsSessionId(selectedId);
                  setStatsTab("metrics");
                  setShowStats(true);
                }}
                className="notify-button stats-launch-button"
                title="Open session statistics"
              >
                <FiBarChart2 size={16} />
                <span>View Statistics</span>
              </button>
            </div>

            {selectedRows[0] && (
              <div className="email-metrics compact">
                <div className="metric">
                  <span className="label">Notifications sent</span>
                  <span className="value">{selectedRows[0].email_sent_count ?? 0}</span>
                </div>
                <div className="metric">
                  <span className="label">Last notification</span>
                  <span className="value">
                    {selectedRows[0].last_email_sent_at
                      ? new Date(selectedRows[0].last_email_sent_at).toLocaleString()
                      : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>

        {false && showStats && (
          <div className="stats-modal-backdrop" onClick={() => setShowStats(false)}>
            <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
              <div className="stats-modal-header">
                <h3>Session Statistics</h3>
                <button
                  className="icon-button secondary"
                  onClick={() => setShowStats(false)}
                >
                  Close
                </button>
              </div>
              <div className="stats-meta">
                <span>
                  Session Length:{" "}
                  {sessionStats.sessionMinutes
                    ? `${sessionStats.sessionMinutes.toFixed(2)} min`
                    : "—"}
                </span>
                <span>Strategy Rows: {sessionStats.totalStrategyRows}</span>
              </div>
              <div className="stats-tab-bar">
                <button
                  className={`stats-tab-btn ${statsTab === "metrics" ? "active" : ""}`}
                  onClick={() => setStatsTab("metrics")}
                >
                  Metrics
                </button>
	                <button
	                  className={`stats-tab-btn ${statsTab === "graph" ? "active" : ""}`}
	                  onClick={() => setStatsTab("graph")}
	                >
	                  Graph
	                </button>
	                <button
	                  className={`stats-tab-btn ${statsTab === "edits" ? "active" : ""}`}
	                  onClick={() => setStatsTab("edits")}
	                >
	                  Edits
	                </button>
	                <button
	                  className={`stats-tab-btn ${statsTab === "responses" ? "active" : ""}`}
	                  onClick={() => setStatsTab("responses")}
	                >
	                  Child Responses
	                </button>
              </div>

              {statsTab === "metrics" ? (
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Value</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="stats-section-row fidelity">
                      <td colSpan={3}>Fidelity Scores</td>
                    </tr>
                    <tr>
                      <td>Overall</td>
                      <td>{formatPercent(sessionStats.fidelityAll.percent)}</td>
                      <td>{`${sessionStats.fidelityAll.sumFours}/${sessionStats.fidelityAll.sumScores}`}</td>
                    </tr>
                    <tr>
                      <td>Time Delay</td>
                      <td>{formatPercent(sessionStats.fidelityTimeDelay.percent)}</td>
                      <td>{`${sessionStats.fidelityTimeDelay.sumFours}/${sessionStats.fidelityTimeDelay.sumScores}`}</td>
                    </tr>
                    <tr>
                      <td>Modeling + Mand-Model</td>
                      <td>{formatPercent(sessionStats.fidelityModelingMand.percent)}</td>
                      <td>{`${sessionStats.fidelityModelingMand.sumFours}/${sessionStats.fidelityModelingMand.sumScores}`}</td>
                    </tr>
                    <tr className="stats-section-row strategy">
                      <td colSpan={3}>Strategy Rates</td>
                    </tr>
                    <tr>
                      <td>Overall</td>
                      <td>{formatRate(sessionStats.rateAll.rate)}</td>
                      <td>{`${sessionStats.rateAll.count} / ${
                        sessionStats.sessionMinutes
                          ? sessionStats.sessionMinutes.toFixed(2)
                          : "—"
                      } min`}</td>
                    </tr>
                    <tr>
                      <td>Time Delay</td>
                      <td>{formatRate(sessionStats.rateTimeDelay.rate)}</td>
                      <td>{`${sessionStats.rateTimeDelay.count} / ${
                        sessionStats.sessionMinutes
                          ? sessionStats.sessionMinutes.toFixed(2)
                          : "—"
                      } min`}</td>
                    </tr>
                    <tr>
                      <td>Modeling + Mand-Model</td>
                      <td>{formatRate(sessionStats.rateModelingMand.rate)}</td>
                      <td>{`${sessionStats.rateModelingMand.count} / ${
                        sessionStats.sessionMinutes
                          ? sessionStats.sessionMinutes.toFixed(2)
                          : "—"
                      } min`}</td>
                    </tr>
                    <tr className="stats-section-row child">
                      <td colSpan={3}>Child Response Rates</td>
                    </tr>
                    <tr>
                      <td>Overall</td>
                      <td>{formatPercent(sessionStats.childRateAll.percent)}</td>
                      <td>{`${sessionStats.childRateAll.responses}/${sessionStats.childRateAll.opportunities}`}</td>
                    </tr>
                    <tr>
                      <td>Time Delay</td>
                      <td>{formatPercent(sessionStats.childRateTimeDelay.percent)}</td>
                      <td>{`${sessionStats.childRateTimeDelay.responses}/${sessionStats.childRateTimeDelay.opportunities}`}</td>
                    </tr>
                    <tr>
                      <td>Modeling + Mand-Model</td>
                      <td>{formatPercent(sessionStats.childRateModelingMand.percent)}</td>
                      <td>{`${sessionStats.childRateModelingMand.responses}/${sessionStats.childRateModelingMand.opportunities}`}</td>
                    </tr>
                  </tbody>
                </table>
              ) : statsTab === "graph" ? (
                <div className="stats-graph-wrap">
                  <div className="stats-legend">
                    <span className="legend-item">
                      <span className="legend-swatch parent" />
                      Parent Overall Fidelity %
                    </span>
                    <span className="legend-item">
                      <span className="legend-swatch child" />
                      Child Responsiveness %
                    </span>
                  </div>
                  {statsBrowseMode && statsDataMode === "original" && originalStatsLoading ? (
                    <p className="muted">Loading original model statistics…</p>
                  ) : statsBrowseMode && statsDataMode === "original" && originalStatsError ? (
                    <p className="muted">{originalStatsError}</p>
                  ) : statsTrendData.length === 0 ? (
                    <p className="muted">No session data available for this user.</p>
                  ) : (
                    <>
                      <svg
                        className="stats-chart"
                        viewBox={`0 0 ${trendChart.width} ${trendChart.height}`}
                        role="img"
                        aria-label="Parent and child performance across sessions"
                      >
                        {trendChart.yTicks.map((tick) => (
                          <g key={tick}>
                            <line
                              x1={trendChart.padLeft}
                              x2={trendChart.width - 20}
                              y1={trendChart.yAt(tick)}
                              y2={trendChart.yAt(tick)}
                              stroke="#e5e7eb"
                              strokeWidth="1"
                            />
                            <text
                              x={trendChart.padLeft - 8}
                              y={trendChart.yAt(tick) + 4}
                              textAnchor="end"
                              fontSize="11"
                              fill="#6b7280"
                            >
                              {tick}
                            </text>
                          </g>
                        ))}

                        <line
                          x1={trendChart.padLeft}
                          x2={trendChart.padLeft}
                          y1={16}
                          y2={trendChart.height - trendChart.padBottom}
                          stroke="#9ca3af"
                          strokeWidth="1.2"
                        />
                        <line
                          x1={trendChart.padLeft}
                          x2={trendChart.width - 20}
                          y1={trendChart.height - trendChart.padBottom}
                          y2={trendChart.height - trendChart.padBottom}
                          stroke="#9ca3af"
                          strokeWidth="1.2"
                        />
                        {activeTrendIndex >= 0 && (
                          <line
                            x1={trendChart.xAt(activeTrendIndex)}
                            x2={trendChart.xAt(activeTrendIndex)}
                            y1={16}
                            y2={trendChart.height - trendChart.padBottom}
                            stroke="#f59e0b"
                            strokeDasharray="4 4"
                            strokeWidth="1.5"
                          />
                        )}

                        {trendChart.parentPath && (
                          <path
                            d={trendChart.parentPath}
                            fill="none"
                            stroke="#2563eb"
                            strokeWidth="2.5"
                          />
                        )}
                        {trendChart.childPath && (
                          <path
                            d={trendChart.childPath}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="2.5"
                          />
                        )}

                        {statsTrendData.map((row, i) => {
                          const x = trendChart.xAt(i);
                          const parentValue = row.parentOverallPercent;
                          const childValue = row.childOverallPercent;
                          const isActiveSession = row.sessionId === activeStatsSessionId;
                          return (
                            <g key={row.sessionId}>
                              {Number.isFinite(parentValue) && (
                                <circle
                                  cx={x}
                                  cy={trendChart.yAt(parentValue)}
                                  r={isActiveSession ? "6" : "4"}
                                  fill="#2563eb"
                                  stroke={isActiveSession ? "#1e3a8a" : "none"}
                                  strokeWidth={isActiveSession ? "2" : "0"}
                                />
                              )}
                              {Number.isFinite(childValue) && (
                                <circle
                                  cx={x}
                                  cy={trendChart.yAt(childValue)}
                                  r={isActiveSession ? "6" : "4"}
                                  fill="#10b981"
                                  stroke={isActiveSession ? "#065f46" : "none"}
                                  strokeWidth={isActiveSession ? "2" : "0"}
                                />
                              )}
                              <text
                                x={x}
                                y={trendChart.height - trendChart.padBottom + 18}
                                textAnchor="middle"
                                fontSize="10"
                                fill={isActiveSession ? "#b45309" : "#6b7280"}
                                fontWeight={isActiveSession ? "700" : "400"}
                              >
                                {`S${row.index}`}
                              </text>
                            </g>
                          );
                        })}
                      </svg>

                      <table className="stats-table stats-session-table">
                        <thead>
                          <tr>
                            <th>Session</th>
                            <th>Date</th>
                            <th>Parent Overall %</th>
                            <th>Child Response %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statsTrendData.map((row) => (
                            <tr
                              key={`stats-${row.sessionId}`}
                              className={row.sessionId === activeStatsSessionId ? "stats-row-active" : ""}
                            >
                              <td>{`S${row.index}`}</td>
                              <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                              <td>{formatPercent(row.parentOverallPercent)}</td>
                              <td>{formatPercent(row.childOverallPercent)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              ) : (
                <div className="stats-edits-wrap">
                  {editsLoading ? (
                    <p className="muted">Loading edit statistics…</p>
                  ) : editsError ? (
                    <p className="muted">{editsError}</p>
                  ) : editsStatsRows.length === 0 ? (
                    <p className="muted">No User Study 2 sessions available for edit statistics.</p>
                  ) : (
                    <table className="stats-table stats-edits-table">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Date</th>
                          <th>Total Rows</th>
                          {EDIT_VARIABLES.map((variable) => (
                            <th key={`hdr-${variable.id}`}>{variable.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {editsStatsRows.map((row) => (
                          <tr
                            key={`edits-${row.sessionId}`}
                            className={row.sessionId === activeStatsSessionId ? "stats-row-active" : ""}
                          >
                            <td>{`S${row.index}`}</td>
                            <td>{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                            <td>{row.totalRows}</td>
                            {EDIT_VARIABLES.map((variable) => {
                              const metric = row.variableStats[variable.id];
                              return (
                                <td key={`cell-${row.sessionId}-${variable.id}`}>
                                  {`${formatPercent(metric.percent)} (${metric.count}/${row.totalRows})`}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={`admin-detail-layout ${isVerticalVideo ? "vertical" : "horizontal"}`}>
          {videoKey && (
            <div className="video-column">
              <VideoPreview
                videoKey={videoKey}
                resumeTime={resumeTime}
                seekRequest={seekRequest}
                onTimeUpdate={(t) =>
                  setVideoTimes((prev) => ({
                    ...prev,
                    [selectedId]: t,
                  }))
                }
                onOrientationChange={setIsVerticalVideo}
                onDurationLoaded={(seconds) =>
                  setVideoDurations((prev) => ({
                    ...prev,
                    [selectedId]: seconds,
                  }))
                }
              />
            </div>
          )}

          <div className="table-column">
            {selectedId && (
              <div className="table-actions">
                <div className="table-actions-group">
                  <button
                    onClick={() => {
                      const fakeId = `new-${Date.now()}`;
                      const newRow = {
                        session_id: selectedId,
                        segment_id: fakeId,
                        begin_end: '',
                        strategy: '',
                        fidelity_score: '',
                        ai_reasoning: '',
                        child_response: '',
                        all_sentences: '',
                        isNew: true
                      };
                      setRows(prev => [newRow, ...prev]);
                      logDebug("Add row clicked", { newRow, prevTop: rows.find(r => r.session_id === selectedId) });
                      setHistoryRows([]);
                      setShowHistory(false);
                    }}
                    className="add-row-btn"
                  >
                    + Add Row
                  </button>
                  <button
                    className="notify-button secondary"
                    disabled={!selectedId || isImporting}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isImporting ? "Importing…" : "Import CSV"}
                  </button>
                  <button
                    className="notify-button secondary"
                    disabled={!selectedId || isImportingSummary}
                    onClick={() => summaryInputRef.current?.click()}
                  >
                    {isImportingSummary ? "Importing…" : "Import Summary JSON"}
                  </button>
                </div>
                <div className="table-actions-group right">
                  <button
                    className="notify-button secondary"
                    onClick={async () => {
                      if (showHistory) {
                        setShowHistory(false);
                        setHistoryRows([]);
                        return;
                      }
                      await fetchHistory(selectedId);
                    }}
                  >
                    {showHistory ? "Hide History" : historyLoading ? "Loading..." : "View History"}
                  </button>
                </div>
              </div>
            )}

            <input
              type="file"
              accept=".csv,.tsv,text/csv,text/tab-separated-values"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!selectedId) {
                  alert("No session selected.");
                  e.target.value = "";
                  return;
                }
                const confirmReplace = window.confirm(
                  "Importing will replace existing analysis rows for this session. Continue?"
                );
                if (!confirmReplace) {
                  e.target.value = "";
                  return;
                }
                setIsImporting(true);
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  await axios.post(
                    `${API}/api/admin/analysis/import/${selectedId}`,
                    formData,
                    {
                      headers: {
                        Authorization: `Bearer ${session.access_token}`,
                      },
                    }
                  );
                  await fetchRows();
                  setHistoryRows([]);
                  setShowHistory(false);
                  alert("Import complete.");
                } catch (err) {
                  console.error("Import failed:", err);
                  alert("Failed to import CSV.");
                } finally {
                  setIsImporting(false);
                  e.target.value = "";
                }
              }}
            />
            <input
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              ref={summaryInputRef}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!selectedId) {
                  alert("No session selected.");
                  e.target.value = "";
                  return;
                }
                const confirmReplace = window.confirm(
                  "Importing will replace the current summary/feedback JSON for this session. Continue?"
                );
                if (!confirmReplace) {
                  e.target.value = "";
                  return;
                }
                setIsImportingSummary(true);
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  await axios.post(
                    `${API}/api/admin/analysis/import-summary/${selectedId}`,
                    formData,
                    {
                      headers: {
                        Authorization: `Bearer ${session.access_token}`,
                      },
                    }
                  );
                  alert("Summary JSON imported.");
                } catch (err) {
                  console.error("Import summary failed:", err);
                  alert("Failed to import summary JSON.");
                } finally {
                  setIsImportingSummary(false);
                  e.target.value = "";
                }
              }}
            />

            <table className="admin-table">
              <thead>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id}>
                    {hg.headers.map(h => (
                      <th key={h.id}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {(() => {
                  let bandIndex = -1;
                  return table.getRowModel().rows.map((r, idx, arr) => {
                    bandIndex += 1;
                    const isHistory = r.original?.isHistory;
                    const isDeleted = r.original?.is_deleted;
                    const prevSeg = idx > 0 ? arr[idx - 1].original?.segment_id : null;
                    const nextSeg = idx < arr.length - 1 ? arr[idx + 1].original?.segment_id : null;
                    const groupStart =
                      showHistory &&
                      !isHistory &&
                      nextSeg &&
                      nextSeg === r.original?.segment_id &&
                      arr[idx + 1].original?.isHistory;
                    const groupClass =
                      isHistory && prevSeg === r.original?.segment_id ? "history-group" : "";
                    const segmentDeleted = isHistory && deletedSegments.has(r.original?.segment_id);
                    const stableKey = `${isHistory ? "h" : "a"}-${r.original.segment_id}-${
                      r.original.historyId || r.original.historyCreatedAt || ""
                    }`;
                    const childResponseDisplay = mapChildResponse(r.original.child_response);
                    const rawChildTranscript = r.original.all_sentences || "";
                    const childTranscript = formatChildTranscript(r.original.child_response, rawChildTranscript);
                    const showChildRow = !isHistory || !!(childResponseDisplay || rawChildTranscript);
                    const columnsCount = table.getAllColumns().length;
                    const childRespEditing = !!editingCells[r.original.segment_id]?.child_response;
                    const childTranscriptEditing = !!editingCells[r.original.segment_id]?.all_sentences;
                    const rowBg = bandIndex % 2 === 0 ? "#fff" : "#f7f7f7";
                    return (
                      <React.Fragment key={stableKey}>
                        <tr
                          className={`${isHistory ? "history-row" : ""} ${groupClass} ${
                            groupStart ? "history-group-start" : ""
                          } ${segmentDeleted ? "history-no-indent" : ""}`}
                          style={{ background: rowBg }}
                          title={
                            isHistory
                              ? `History (${r.original.historyAction || "past"})${isDeleted ? " – deleted" : ""}`
                              : undefined
                          }
                        >
                          {r.getVisibleCells().map(c => (
                            <td key={c.id}>
                              {flexRender(c.column.columnDef.cell, c.getContext())}
                              {isHistory && c.column.id === "begin_end" && (
                                <span className="history-badge">
                                  {r.original.historyAction === "delete"
                                    ? "Deleted"
                                    : r.original.historyAction === "restore"
                                    ? "Restored"
                                    : r.original.historyAction === "update"
                                    ? "Edited"
                                    : r.original.historyAction === "original"
                                    ? "Original"
                                    : "Edited"}
                                  {r.original.historyCreatedAt && (
                                    <span className="history-timestamp">
                                      {new Date(r.original.historyCreatedAt).toLocaleString()}
                                    </span>
                                  )}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                        {showChildRow && (
                          <tr
                            className={`child-row ${isHistory ? "history-row" : ""}`}
                            style={{ background: rowBg }}
                          >
                            <td colSpan={columnsCount}>
                              <div className="child-row-content">
                                <div className="child-field">
                                  <strong>Child Response:</strong>{" "}
                                  <EditableCell
                                    initial={childResponseDisplay}
                                    onSave={(v) => save(r.original.segment_id, { child_response: v })}
                                    type="child-response"
                                    options={CHILD_RESPONSE_OPTIONS}
                                    readOnly={isHistory}
                                    isEditing={childRespEditing}
                                    onStart={() => startEditCell(r.original.segment_id, "child_response")}
                                    onStop={(id) => stopEditCell(id || r.original.segment_id, "child_response")}
                                  />
                                </div>
                                <div className="child-field">
                                  <strong>Child transcript:</strong>{" "}
                                  <EditableCell
                                    initial={rawChildTranscript}
                                    displayValue={childTranscript}
                                    onSave={(v) => save(r.original.segment_id, { all_sentences: v })}
                                    readOnly={isHistory}
                                    isEditing={childTranscriptEditing}
                                    onStart={() => startEditCell(r.original.segment_id, "all_sentences")}
                                    onStop={(id) => stopEditCell(id || r.original.segment_id, "all_sentences")}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </div>
        </>
      ) : (
        <>
          <button className="back-button" onClick={() => {
              setSelectedId(null);
              navigate("/admin", { replace: true });
            }}>
            ← Back
          </button>
          <h2>
            {new Date(selectedRows[0]?.created_at).toLocaleString()} — {selectedRows[0]?.user_name}
            <span className="study-badge" style={{ marginLeft: "0.75rem" }}>{getStudyLabel(selectedRows[0]?.created_at)}</span>
            {getSessionPhaseMeta(selectedRows[0]?.session_phase) ? (
              <span
                className={`session-phase-badge phase-${getSessionPhaseMeta(selectedRows[0]?.session_phase).id}`}
                style={{ marginLeft: "0.75rem" }}
              >
                {getSessionPhaseMeta(selectedRows[0]?.session_phase).label}
              </span>
            ) : null}
          </h2>
          <div className="admin-actions-bar">
            <div className="view-toggle">
              {!isReliabilityOnly && (
                <button
                  className={`notify-button ${adminTab === "analysis" ? "" : "secondary"}`}
                  onClick={() => setAdminTab("analysis")}
                >
                  Analysis
                </button>
              )}
              <button
                className={`notify-button ${adminTab === "reliability" ? "" : "secondary"}`}
                onClick={() => setAdminTab("reliability")}
              >
                Reliability (Videos Only)
              </button>
            </div>
            <div className="admin-actions-right">
              <button className="notify-button secondary" onClick={handleSwitchToMain}>
                Switch to Main
              </button>
              <button className="notify-button" onClick={handleSignOut}>
                Sign Out
              </button>
            </div>
          </div>
          <div className={`admin-detail-layout ${isVerticalVideo ? "vertical" : "horizontal"}`}>
            {videoKey && (
              <div className="video-column">
                <VideoPreview
                  videoKey={videoKey}
                  seekRequest={seekRequest}
                  onOrientationChange={setIsVerticalVideo}
                  onDurationLoaded={(seconds) =>
                    setVideoDurations((prev) => ({
                      ...prev,
                      [selectedId]: seconds,
                    }))
                  }
                />
              </div>
            )}
            <div className="table-column">
              <div className="email-metrics">
                <div className="metric">
                  <span className="label">User</span>
                  <span className="value">{selectedRows[0]?.user_email || "—"}</span>
                </div>
                <div className="metric">
                  <span className="label">Session</span>
                  <span className="value">{selectedId}</span>
                </div>
              </div>
              <p className="muted" style={{ marginTop: "1rem" }}>
                Analysis is hidden in Reliability mode.
              </p>
            </div>
          </div>
        </>
      ))}
    </div>
  );
}
